import CosmoClawWebSocket from '@/views/smart-workbench/cosmoClawWebsocket'
import { setAuthorizationApi } from '@/api/agentAuth'
import { Message } from 'element-ui'
import Cookies from 'js-cookie'
import { appendMockMessageApi } from '@/api/agentAuth'
import { getKnowledgeClientPageApi } from '@/api/agentAuth'
import { getAgentModelsApi } from '@/api/agentAuth'
import { getToken } from '@/utils/auth'
import { validateSphereUserSeat, isSphereSeatValid } from '@/api/sphereUser'
import { wsTrace, wsDrift } from '@/utils/wsDebugTrace'

// 统一智能体 ID 映射（sessionKey 格式: agent:agentId:prefix|sessionId）
// ID 从 .env 文件中读取，按环境切换（test / pre）
// 未设置环境变量时使用 test 环境 ID 作为默认值
// AGENT_ID_MAP 初始为空，由 buildAgentIdMap() 在获取 agent 列表后动态填充
export const AGENT_ID_MAP = {
  ceo_shu_zi_ren: '',
  cai_wu_shu_zi_ren: '',
  gong_ying_lian_shu_zi_ren: '',
  dh_ny_dh: '',
  zhi_biao_ding_yi_zhi_neng_ti: '',
  ben_ti_zhi_li_zhi_neng_ti: '',
  wan_xiang_guan_shu_zhi_neng_ti: '',
  jing_ying_jue_ce_zhi_neng_ti: ''
}

// 标签短码 → 内部 agentName 映射
// 用于从 API 返回的 tags 字段识别智能体/数字人类型
export const TAG_TO_INTERNAL_NAME = {
  'CEO-DH': 'ceo_shu_zi_ren',
  'Fin-DH': 'cai_wu_shu_zi_ren',
  'SC-DH': 'gong_ying_lian_shu_zi_ren',
  'NY-DH': 'dh_ny_dh',
  IDA: 'zhi_biao_ding_yi_zhi_neng_ti',
  OGA: 'ben_ti_zhi_li_zhi_neng_ti',
  PDA: 'wan_xiang_guan_shu_zhi_neng_ti',
  ODA: 'jing_ying_jue_ce_zhi_neng_ti'
}

// 内部 agentName → 标签短码 反向映射
const INTERNAL_NAME_TO_TAG = {}
for (const [tag, name] of Object.entries(TAG_TO_INTERNAL_NAME)) {
  INTERNAL_NAME_TO_TAG[name] = tag
}

// 内部 agentName → 类型（digital-human / agent）：tags 缺失类型标签时据此推断，
// 保证后续新增同 internalName 多实例（如 OGA / OGA,YS）即使只带短码也能正确识别类型
export const INTERNAL_NAME_TO_TYPE = {
  ceo_shu_zi_ren: 'digital-human',
  cai_wu_shu_zi_ren: 'digital-human',
  gong_ying_lian_shu_zi_ren: 'digital-human',
  dh_ny_dh: 'digital-human',
  zhi_biao_ding_yi_zhi_neng_ti: 'agent',
  ben_ti_zhi_li_zhi_neng_ti: 'agent',
  wan_xiang_guan_shu_zhi_neng_ti: 'agent',
  jing_ying_jue_ce_zhi_neng_ti: 'agent'
}

// agentList 快照缓存：供 agentId → internalName 反向解析（多实例共存时不能只靠单值 AGENT_ID_MAP）
let _agentListCache = []

/**
 * 从 API 返回的 agentList 动态构建 AGENT_ID_MAP
 * @param {Array} agentList - API 返回的智能体列表
 */
export function buildAgentIdMap(agentList) {
  if (!Array.isArray(agentList)) return
  _agentListCache = agentList
  // 每次重建都从最新列表重新计算，避免上一次的实例被禁用后仍被兜底命中
  for (const k of Object.keys(AGENT_ID_MAP)) AGENT_ID_MAP[k] = ''
  // AGENT_ID_MAP 是「单值兜底映射」：同一 internalName 存在多个实例
  // （如 CEO-DH / CEO-DH,GC / CEO-DH,YS）时，只保留第一个「启用」实例作为默认兜底，
  // 与菜单高亮 / getAgentIdByInternalName / 默认 agentId 解析的「首个启用实例」约定保持一致，
  // 避免兜底路径（未显式传 agentId 时）命中禁用或末位实例导致串号。
  for (const agent of agentList) {
    if (!agent.id || !agent.tags) continue
    if (agent.enabled === false || agent.deleted !== 0) continue
    const tags =
      typeof agent.tags === 'string'
        ? agent.tags.split(',').map((t) => t.trim())
        : Array.isArray(agent.tags)
          ? agent.tags
          : []
    for (const tag of tags) {
      const internalName = TAG_TO_INTERNAL_NAME[tag]
      if (internalName && !AGENT_ID_MAP[internalName]) {
        AGENT_ID_MAP[internalName] = agent.id
        break
      }
    }
  }
}

/**
 * 从 agentList 中按内部名称查找所有匹配实例（支持多个同类型数字人/智能体共存）
 * @returns {Array} 匹配的 agent 列表
 */
export function findAgentsByInternalName(internalName, agentList) {
  if (!internalName) return []
  const list = Array.isArray(agentList) ? agentList : []
  return list.filter((a) => {
    const { internalName: name } = parseAgentTags(a.tags, a.name)
    return name === internalName
  })
}

/**
 * 从 agentList 中按内部名称自动匹配「当前数字人/智能体」：
 * 优先返回启用的实例（enabled !== false 且未删除），否则返回第一个匹配实例
 */
export function findAgentByInternalName(internalName, agentList) {
  const list = findAgentsByInternalName(internalName, agentList)
  return (
    list.find((a) => a.enabled !== false && a.deleted === 0) || list[0] || null
  )
}

// 已知「非短码」标签：类型/变体标签不参与 internalName 解析，
// 兜底生成 internalName 时需跳过，避免把 digital-human/agent/YS/GC/LQZ 当成数字人短码
const TYPE_TAG_SET = new Set(['digital-human', 'agent'])
const VARIANT_TAG_SET = new Set(['YS', 'GC', 'LQZ'])

// 判定 tag 是否像数字人/智能体短码（如 CEO-DH / NY-DH / OGA / IDA），用于未知短码的兜底 internalName
function isCodeLikeTag(t) {
  return (
    !!t &&
    !TYPE_TAG_SET.has(t) &&
    !VARIANT_TAG_SET.has(t) &&
    /^[A-Za-z][A-Za-z0-9]*(?:-[A-Za-z0-9]+)*$/.test(t)
  )
}

// 新增数字人/智能体的短码不在 TAG_TO_INTERNAL_NAME 时，生成稳定的兜底 internalName
// （如 digital-human,NY-DH → dh_ny_dh，agent,ABC → agent_abc），保证「菜单完全由 agentList
// 驱动」时新增实例（tags 未知短码）也能上菜单；无短码时退化为从名称生成。
function synthesizeInternalName(type, tagList, name) {
  const codeTag = (tagList || []).find(isCodeLikeTag) || ''
  const source = codeTag || (name && String(name).trim()) || ''
  let slug = String(source)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  // 名称非 ASCII（如中文）导致 slug 为空时，给固定后缀兜底，避免该实例被列表过滤掉
  if (!slug) {
    if (!source) return null
    slug = 'custom'
  }
  const prefix = type === 'digital-human' ? 'dh' : 'agent'
  return prefix + '_' + slug
}

/**
 * 解析 tags 字段，返回分类信息
 */
export function parseAgentTags(tags, name) {
  const tagList =
    typeof tags === 'string'
      ? tags.split(',').map((t) => t.trim())
      : Array.isArray(tags)
        ? tags
        : []
  let type = ''
  let tagCode = null
  let internalName = null
  const isDemo = tagList.includes('YS')
  const isGc = tagList.includes('GC')
  const isLqz = tagList.includes('LQZ')
  if (tagList.includes('digital-human')) {
    type = 'digital-human'
    for (const t of tagList) {
      if (TAG_TO_INTERNAL_NAME[t]) {
        tagCode = t
        internalName = TAG_TO_INTERNAL_NAME[t]
        break
      }
    }
    // 未知短码（如 NY-DH）也能识别：生成稳定兜底 internalName，
    // 保证新增数字人出现在左侧菜单/列表（此前会被 digitalHumanList 过滤掉）
    if (!internalName) internalName = synthesizeInternalName(type, tagList, name)
  } else if (tagList.includes('agent')) {
    type = 'agent'
    for (const t of tagList) {
      if (TAG_TO_INTERNAL_NAME[t]) {
        tagCode = t
        internalName = TAG_TO_INTERNAL_NAME[t]
        break
      }
    }
    // 同上：新增智能体（未知短码）也能上菜单
    if (!internalName) internalName = synthesizeInternalName(type, tagList, name)
  }
  // 类型标签（digital-human/agent）缺失时，按短码解析 internalName 并用映射推断类型，
  // 兼容后续只带短码的新增实例
  if (!type && !internalName) {
    for (const t of tagList) {
      if (TAG_TO_INTERNAL_NAME[t]) {
        tagCode = t
        internalName = TAG_TO_INTERNAL_NAME[t]
        break
      }
    }
    if (internalName) type = INTERNAL_NAME_TO_TYPE[internalName] || ''
  }
  return { type, tagCode, internalName, isDemo, isGc, isLqz }
}
// 从最新 agentList 解析 internalName 对应的数字 agentId（响应式，列表刷新后自动更新）
export function getAgentIdByInternalName(internalName, agentList) {
  if (!internalName) return ''
  // 优先返回第一个启用的匹配实例（与菜单高亮 digitalHumanList/myAgentList 同源），
  // 避免同 internalName 多实例时 AGENT_ID_MAP 最后实例胜出导致菜单/页面错位
  const item = findAgentByInternalName(internalName, agentList)
  return (item && item.id) || AGENT_ID_MAP[internalName] || ''
}

/**
 * 通过名称关键词猜测内部 agentName（兜底逻辑）
 */

// 反向映射：agentId → agentName
// 先查 AGENT_ID_MAP 单值兜底，再从 agentList 快照动态匹配
// （支持多实例：真实 id 不在单值映射中也能解析出 internalName）
function getAgentNameFromId(agentId) {
  if (!agentId) return null
  for (const [name, id] of Object.entries(AGENT_ID_MAP)) {
    if (id && String(id) === String(agentId)) return name
  }
  if (_agentListCache && _agentListCache.length) {
    const item = _agentListCache.find((a) => String(a.id) === String(agentId))
    if (item) {
      const { internalName } = parseAgentTags(item.tags, item.name)
      if (internalName) return internalName
    }
  }
  return null
}

function getAgentIdFromName(name) {
  // 多实例共存时优先从最新 agentList 取「首个启用实例」，与菜单高亮 / 默认 agentId 解析约定一致，
  // 避免 AGENT_ID_MAP 单值兜底串到其它实例（如 CEO 普通/高管/演示、OGA/OGA,YS 互相串号）
  if (name && _agentListCache && _agentListCache.length) {
    const item = findAgentByInternalName(name, _agentListCache)
    if (item && item.id) return item.id
  }
  return AGENT_ID_MAP[name] || null
}

// Silent tokens to filter from display
const SILENT_TOKENS = ['NO_REPLY', 'NO_RE', 'HEARTBEAT_OK']

// Serialize mock-message writes to preserve message order.
let mockMessagePersistQueue = Promise.resolve()

function extractLastIframeItems(messages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]
    if (msg.type !== 'assistant' || !msg.content) continue
    const regex = /\{\s*"component"\s*:\s*"iframe"[\s\S]*?\}/g
    let match
    let lastItems = null
    while ((match = regex.exec(msg.content)) !== null) {
      try {
        let jsonStr = match[0]
        const braceStart = msg.content.indexOf('{', match.index)
        let depth = 0
        let inStr = false
        let esc = false
        let end = -1
        for (let j = braceStart; j < msg.content.length; j++) {
          const ch = msg.content[j]
          if (inStr) {
            if (esc) {
              esc = false
              continue
            }
            if (ch === '\\') {
              esc = true
              continue
            }
            if (ch === '"') {
              inStr = false
            }
            continue
          }
          if (ch === '"') {
            inStr = true
            continue
          }
          if (ch === '{') {
            depth++
            continue
          }
          if (ch === '}') {
            depth--
            if (depth === 0) {
              end = j
              break
            }
          }
        }
        if (end !== -1) {
          jsonStr = msg.content.slice(braceStart, end + 1)
        }
        const parsed = JSON.parse(jsonStr)
        if (parsed && parsed.component === 'iframe') {
          let items = parsed.items || (parsed.props && parsed.props.items)
          if (typeof items === 'string') {
            try {
              items = JSON.parse(items)
            } catch {
              items = []
            }
          }
          if (Array.isArray(items) && items.length > 0) {
            lastItems = items
          }
        }
      } catch {
        /* ignore parse errors */
      }
    }
    if (lastItems) return lastItems
  }
  return null
}

function stripSilentMarkers(text) {
  let result = text
  SILENT_TOKENS.forEach((token) => {
    while (result.includes(token)) {
      result = result.replace(token, '')
    }
  })
  return result.trim()
}

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const wsManagers = {}
let sharedWs = null
const sessionPollTimers = {}
// 在途的 chat.history 请求：key = agentId::sessionKey，value = 请求 Promise。
// SessionChatPage 挂载时多个 watcher（$route immediate / mounted / sessionsLoading /
// agentListLoaded 等）会在首个响应返回前重复触发 switchSession -> loadHistory，
// 通过复用同一个在途 Promise 保证同一会话的 chat.history 只发送一次。
const historyPending = new Map()

function createDefaultAgentData() {
  return {
    socketState: 'DISCONNECTED',
    isHandshaked: false,
    currentSessionKey: null,
    sessions: [],
    sessionMessages: {},
    sessionIsStreaming: {},
    sessionRunIds: {},
    sessionAgentEnded: {},
    isAborting: false,
    isFinishing: false,
    lastAssistantText: '',
    agentEnded: false,
    globalError: null,
    models: [],
    modelList: [],
    currentModel: '',
    gatewayToken: '',
    gatewayUrl: ''
  }
}

function initAgentData(state, agentId) {
  if (!state.agentData[agentId]) {
    state.agentData[agentId] = createDefaultAgentData()
  }
}

function getEffectiveWs(agentId) {
  return sharedWs || wsManagers[agentId] || null
}

/**
 * 集中同步共享 WS 的路由标识：store.currentAgentId 是唯一事实来源，
 * sharedWs.agentId 只是它的投影。
 * 在此统一监听，避免新增数字人/新增进入入口时漏同步，导致事件按旧 agent 判断会话归属
 * （表现为 WS 已回数据但页面不渲染）。
 * store.watch 是异步批处理的，不会打断正在进行的 mutation 序列；
 * 若仍需同步生效（如发送前），sendMessage 等处保留的即时同步作为兜底。
 */
export function bindSharedWsAgentSync(store) {
  return store.watch(
    (state) => (state.cosmoClaw && state.cosmoClaw.currentAgentId) || '',
    (agentId) => {
      if (!sharedWs || !agentId || sharedWs.agentId === agentId) return
      sharedWs.setAgentId(agentId)
    }
  )
}

// 解析 chat.send 所需的模型参数：modelName / modelProvider 取自当前会话对应模型
// （按 modelList 中 id 匹配会话 model，缺省回退到 currentModel）的 name / provider 字段
function resolveChatModelParams(state, sessionKey) {
  const modelList = state.modelList || []
  const session = (state.sessions || []).find((s) => s.key === sessionKey)
  // 缺省回退：会话未记录模型且未显式选择时，用模型列表第一个（与输入框默认展示的选中一致），
  // 保证新会话首条 chat.send 也能带上 modelName / modelProvider
  const fallbackModel = modelList.length > 0 ? modelList[0] : null
  let modelId =
    (session && session.model) ||
    state.currentModel ||
    (fallbackModel && fallbackModel.id) ||
    ''
  let model = modelList.find((m) => m.id === modelId)
  // 历史会话可能采用了新模型列表中已不存在的模型：自动回退到模型列表第一个
  if (!model && fallbackModel) {
    modelId = fallbackModel.id
    model = fallbackModel
  }
  return {
    modelName: (model && model.name) || '',
    modelProvider: (model && model.provider) || ''
  }
}

// 解析 chat.send 所需的知识库参数：开关状态 + 当前数字人知识库 id
// Read the knowledge switch state bound to the current session (or the no-session draft); default off
function getKnowledgeSwitchEnabled(state) {
  const map = state.knowledgeSwitchBySession || {}
  const key = state.currentSessionKey || ''
  if (key && key in map) return map[key] ? 1 : 0
  if ('' in map) return map[''] ? 1 : 0
  return 0
}

function resolveKnowledgeParams(state) {
  return {
    // 仅在启用且存在知识库 id 时才传 enable=1，避免空 knowledgeId 误开启
    enableKnowledgeBase: getKnowledgeSwitchEnabled(state) && state.knowledgeId ? 1 : 0,
    knowledgeId: state.knowledgeId || ''
  }
}

// 从知识库接口响应中提取知识库 id（兼容单对象 / 分页 / documentList 多种结构）
function extractKnowledgeIdFromRes(res) {
  const body = (res && res.data) || res || {}
  const data = body.data || body
  const pick = (obj) =>
    (obj &&
      (obj.ragflowId || obj.kbId || obj.id || obj.knowledgeId || obj.datasetId || obj.kb_id || obj.ragflow_id || '')) ||
    ''
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const direct = pick(data)
    if (direct) return direct
    const dl = data.documentList
    if (Array.isArray(dl)) {
      const first = pick(dl[0])
      if (first) return first
    } else if (dl && typeof dl === 'object') {
      const first = pick(dl.records && dl.records[0])
      if (first) return first
      const dlDirect = pick(dl)
      if (dlDirect) return dlDirect
    }
    const rows = data.rows || data.list || data.records || data.items || []
    const list = Array.isArray(rows) ? rows : []
    if (list.length > 0) {
      const first = pick(list[0])
      if (first) return first
    }
    return ''
  }
  if (Array.isArray(data)) {
    return pick(data[0]) || ''
  }
  return ''
}

// 本地 demo/mock 消息直接 commit 到 flat state（ADD_MESSAGE）时，同步镜像到当前 agent 的会话缓存，
// 避免切换会话后再切回时这些消息丢失
function syncFlatMessageToSessionCache(state, message) {
  const agentId = state.currentAgentId
  // 优先使用消息自带的 _sessionKey（demo 定时器在切换会话后仍能写入原会话 cache），
  // 否则 fallback 到当前会话
  const sessionKey = message._sessionKey || state.currentSessionKey
  if (!agentId || !sessionKey) return
  initAgentData(state, agentId)
  const cache = state.agentData[agentId].sessionMessages
  if (!cache[sessionKey]) cache[sessionKey] = []
  // 同 id 已存在则合并更新，避免重复插入
  if (message.id) {
    const existIdx = cache[sessionKey].findIndex((m) => m.id === message.id)
    if (existIdx !== -1) {
      cache[sessionKey][existIdx] = {
        ...cache[sessionKey][existIdx],
        ...message
      }
      return
    }
  }
  cache[sessionKey].push(message)
}

const cosmoClaw = {
  namespaced: true,
  state: {
    socketState: 'DISCONNECTED',
    isHandshaked: false,
    messages: [],
    sessions: [],
    currentSessionKey: null,
    models: [],
    modelList: [],
    currentModel: '',
    knowledgeId: '', // 当前数字人的知识库 id
    knowledgeSwitchBySession: {}, // knowledge switch bound per session: { sessionKey: 0|1 }; empty key = no-session draft
    inputDrafts: {}, // 各会话输入框草稿：{ sessionKey: text }，切换历史会话后保留未发送内容
    currentRunId: null,
    sessionIsLive: false,
    isStreaming: false,
    isAborting: false,
    isSwitchingSession: false,
    isFinishing: false,
    showTools: true,
    accessUrl: null,
    token: null,
    lastAssistantText: '',
    agentEnded: false,
    globalError: null,
    drawerVisible: false,
    drawerConfig: {},
    cardSelections: {},
    taskProgressList: [],
    taskProgressVisible: false,
    iframeItems: [],
    iframeVisible: false,
    agentPlazaVisible: false,
    iframeActiveIndex: 0,
    centerIframeItem: null,
    postMessagePayload: null,
    localDemoMode: false,
    isCeoDemo: false,
    isGc: false,
    isLqz: false,
    // 智能体首页跳转发送时暂存的附件（由会话页 sendAfter 流程消费后清空）
    pendingFiles: [],
    sessionsLoading: false,
    sessionsLocked: false,
    currentAgentName: null,
    currentAgentId: null,
    agentData: {},
    _sessionOrder: []
  },
  mutations: {
    SET_SOCKET_STATE(state, value) {
      state.socketState = value
    },
    SET_IS_HANDSHAKED(state, value) {
      state.isHandshaked = value
    },
    SET_MESSAGES(state, messages) {
      state.messages = Array.isArray(messages) ? [...messages] : messages
    },
    RESET_ALL_SESSIONS(state) {
      state.messages = []
      state.sessions = []
      state.currentSessionKey = null
      state.agentData = {}
      state._sessionOrder = []
      state.currentAgentId = null
      state.currentAgentName = null
      state.knowledgeId = ''
      state.knowledgeSwitchBySession = {}
      state.inputDrafts = {}
      state.lastAssistantText = ''
      state.agentEnded = false
      state.globalError = null
    },
    ADD_MESSAGE(state, message) {
      // 给消息打上所属会话 key，切换会话后 demo 定时器仍能写入原会话 cache
      if (!message._sessionKey) {
        message._sessionKey = state.currentSessionKey
      }
      const isCurrentSession = message._sessionKey === state.currentSessionKey
      // 流式输出时去重：同 toolCallId 的工具消息只保留一份，合并更新
      if (message.toolCallId) {
        const existIdx = state.messages.findIndex(
          (m) => m.toolCallId === message.toolCallId && m.isStreaming
        )
        if (existIdx !== -1) {
          const merged = {
            ...state.messages[existIdx],
            ...message
          }
          state.messages.splice(existIdx, 1, merged)
          if (!message._noCache) {
            syncFlatMessageToSessionCache(state, merged)
          }
          return
        }
      }
      // 只有属于当前会话的消息才加入 flat state；
      // 切换到其他会话后 demo 追加的消息只写入原会话 cache，不污染当前会话视图
      if (isCurrentSession) {
        state.messages.push(message)
      }
      // _noCache 标记的消息（如深度思考卡片、临时气泡）不写入会话缓存，
      // 避免切换会话后恢复时卡在初始 loading 状态或显示过时的中间过程
      if (!message._noCache) {
        syncFlatMessageToSessionCache(state, message)
      }
    },
    UPDATE_MESSAGE_CONTENT_BY_ID(state, { id, content, richHtml }) {
      const idx = state.messages.findIndex(m => m.id === id)
      if (idx !== -1) {
        // 创建新对象替换，不修改原对象引用
        const updated = {
          ...state.messages[idx],
          content,
          richHtml: richHtml !== undefined ? richHtml : state.messages[idx].richHtml
        }
        state.messages.splice(idx, 1, updated)
        // 同步到 sessionCache：切换会话再切回来时内容保持一致
        syncFlatMessageToSessionCache(state, updated)
        return
      }
      // flat state 中找不到（可能切换了会话），回退遍历所有 session cache 查找，
      // 确保 demo 定时器在切换会话后仍能更新原会话的消息内容
      const agentId = state.currentAgentId
      if (agentId && state.agentData[agentId]) {
        const sessionMessages = state.agentData[agentId].sessionMessages
        for (const sk of Object.keys(sessionMessages)) {
          const cache = sessionMessages[sk]
          const cidx = cache.findIndex(m => m.id === id)
          if (cidx !== -1) {
            const updated = {
              ...cache[cidx],
              content,
              richHtml: richHtml !== undefined ? richHtml : cache[cidx].richHtml
            }
            cache[cidx] = updated
            // 若该消息属于当前会话，同步更新 flat state
            if (sk === state.currentSessionKey) {
              state.messages.splice(
                state.messages.findIndex(m => m.id === id),
                1,
                updated
              )
            }
            return
          }
        }
      }
    },
    REMOVE_MESSAGE_BY_ID(state, id) {
      const idx = state.messages.findIndex(m => m.id === id)
      if (idx !== -1) {
        state.messages.splice(idx, 1)
        // 同步清理 sessionCache 中的对应消息
        const agentId = state.currentAgentId
        const sessionKey = state.currentSessionKey
        if (agentId && sessionKey) {
          const cache = state.agentData[agentId] && state.agentData[agentId].sessionMessages
          if (cache && cache[sessionKey]) {
            const cacheIdx = cache[sessionKey].findIndex(m => m.id === id)
            if (cacheIdx !== -1) {
              cache[sessionKey].splice(cacheIdx, 1)
            }
          }
        }
      }
    },
    UPDATE_LAST_MESSAGE_CONTENT(state, content) {
      for (let i = state.messages.length - 1; i >= 0; i--) {
        if (
          state.messages[i].type === 'assistant' &&
          state.messages[i].isStreaming &&
          !state.messages[i]._finalized
        ) {
          state.messages[i].content = content
          return
        }
      }
    },
    UPDATE_TOOL_MESSAGE(state, { toolCallId, updates }) {
      let idx = -1
      if (toolCallId) {
        idx = state.messages.findIndex((m) => m.toolCallId === toolCallId)
      }
      if (idx === -1) {
        // 兜底：toolCallId 缺失/失配时，更新最近一条仍未结束的工具调用
        for (let i = state.messages.length - 1; i >= 0; i--) {
          if (state.messages[i].type === 'toolCall' && state.messages[i].phase !== 'result') {
            idx = i
            break
          }
        }
      }
      if (idx !== -1) {
        Object.assign(state.messages[idx], updates)
      }
    },
    INSERT_BEFORE_STREAMING_ASSISTANT(state, message) {
      for (let i = state.messages.length - 1; i >= 0; i--) {
        if (
          state.messages[i].type === 'assistant' &&
          state.messages[i].isStreaming
        ) {
          state.messages.splice(i, 0, message)
          return
        }
      }
      state.messages.push(message)
    },
    SPLIT_AT_TOOL_CALL(state, toolMsg) {
      // 去重：同 toolCallId 已存在则合并更新，不重复插入
      if (toolMsg.toolCallId) {
        const existIdx = state.messages.findIndex(
          (m) => m.toolCallId === toolMsg.toolCallId
        )
        if (existIdx !== -1) {
          Object.assign(state.messages[existIdx], toolMsg)
          return
        }
      }
      // 新的工具调用出现说明前面的工具已结束（串行执行）：先标记仍未结束的工具
      state.messages.forEach((m) => {
        if (m.type === 'toolCall' && m.phase !== 'result') {
          m.phase = 'result'
          m.isStreaming = false
          if (m.toolName) m.content = `Tool ${m.toolName} completed`
        }
      })
      for (let i = state.messages.length - 1; i >= 0; i--) {
        if (
          state.messages[i].type === 'assistant' &&
          state.messages[i].isStreaming
        ) {
          state.messages[i].isStreaming = false
          state.messages[i]._finalized = true
          state.messages.splice(i + 1, 0, toolMsg)
          return
        }
      }
      state.messages.push(toolMsg)
    },
    SET_STREAMING_DONE(state) {
      state.messages.forEach((m) => {
        if (m.isStreaming) {
          m.isStreaming = false
          if (m.content) {
            m.content = stripSilentMarkers(m.content)
          }
        }
        if (m.type === 'toolCall' && m.phase !== 'result') {
          m.phase = 'result'
          if (m.toolName) m.content = `Tool ${m.toolName} completed`
        }
      })
      state.isStreaming = false
      state.isAborting = false
      state.currentRunId = null
      state.sessionIsLive = false
      state.lastAssistantText = ''
      state.agentEnded = false
      state.isFinishing = false
    },
    FINALIZE_PREVIOUS_STREAMING(state) {
      for (let i = state.messages.length - 1; i >= 0; i--) {
        const m = state.messages[i]
        if (m.type === 'assistant' && m.isStreaming) {
          m.isStreaming = false
          m._finalized = true
          if (m.content) {
            m.content = stripSilentMarkers(m.content)
          }
          break
        }
      }
    },
    SET_SESSIONS(state, sessions) {
      state.sessions = sessions
    },
    SET_SESSION_ORDER(state, order) {
      state._sessionOrder = order
    },
    SET_CURRENT_SESSION_KEY(state, key) {
      state.currentSessionKey = key
    },
    SET_MODELS(state, { models, modelList }) {
      state.models = models
      state.modelList = modelList
    },
    SET_CURRENT_MODEL(state, model) {
      state.currentModel = model
    },
    SET_KNOWLEDGE_ID(state, id) {
      state.knowledgeId = id || ''
    },
    SET_KNOWLEDGE_SWITCH(state, { sessionKey, value }) {
      const map = Object.assign({}, state.knowledgeSwitchBySession || {})
      const key = sessionKey || state.currentSessionKey || ''
      map[key] = value ? 1 : 0
      state.knowledgeSwitchBySession = map
    },
    MIGRATE_KNOWLEDGE_SWITCH_DRAFT(state) {
      const map = state.knowledgeSwitchBySession || {}
      const key = state.currentSessionKey || ''
      if (!key || !('' in map) || key in map) return
      const next = Object.assign({}, map)
      next[key] = next['']
      delete next['']
      state.knowledgeSwitchBySession = next
    },
    // 保存/清除某个会话的输入框草稿（text 为空时删除该会话草稿）
    SET_INPUT_DRAFT(state, { sessionKey, text }) {
      const key = sessionKey || ''
      const drafts = Object.assign({}, state.inputDrafts || {})
      if (text) {
        drafts[key] = text
      } else {
        delete drafts[key]
      }
      state.inputDrafts = drafts
    },
    SET_CURRENT_RUN_ID(state, id) {
      state.currentRunId = id
    },
    SET_IS_STREAMING(state, val) {
      state.isStreaming = val
    },
    SET_SESSION_IS_LIVE(state, val) {
      state.sessionIsLive = val
    },
    SET_IS_ABORTING(state, val) {
      state.isAborting = val
    },
    SET_IS_SWITCHING_SESSION(state, val) {
      state.isSwitchingSession = val
    },
    SET_SHOW_TOOLS(state, show) {
      state.showTools = show
    },
    SET_GLOBAL_ERROR(state, error) {
      state.globalError = error
    },
    SET_IS_FINISHING(state, val) {
      state.isFinishing = val
    },
    SET_LAST_ASSISTANT_TEXT(state, text) {
      state.lastAssistantText = text
    },
    SET_AGENT_ENDED(state, val) {
      state.agentEnded = val
    },
    SET_MEDIA_URLS(state, urls) {
      for (let i = state.messages.length - 1; i >= 0; i--) {
        if (
          state.messages[i].type === 'assistant' &&
          state.messages[i].isStreaming
        ) {
          state.messages[i]._mediaUrls = urls
          return
        }
      }
    },
    SET_TOKEN(state, token) {
      state.token = token
    },
    SET_ACCESS_URL(state, url) {
      state.accessUrl = url
    },
    SET_DRAWER_VISIBLE(state, val) {
      state.drawerVisible = val
    },
    SET_DRAWER_CONFIG(state, config) {
      state.drawerConfig = config
    },
    SET_CARD_SELECTION(state, { key, value }) {
      state.cardSelections = { ...state.cardSelections, [key]: value }
    },
    SET_TASK_PROGRESS_LIST(state, list) {
      state.taskProgressList = list
    },
    SET_TASK_PROGRESS_VISIBLE(state, val) {
      state.taskProgressVisible = val
    },
    SET_IFRAME_ITEMS(state, items) {
      state.iframeItems = items
    },
    SET_IFRAME_VISIBLE(state, val) {
      state.iframeVisible = val
    },
    SET_IFRAME_ACTIVE_INDEX(state, idx) {
      state.iframeActiveIndex = idx
    },
    SET_AGENT_PLAZA_VISIBLE(state, val) {
      state.agentPlazaVisible = val
    },
    SET_CENTER_IFRAME(state, item) {
      state.centerIframeItem = item
    },
    SET_POST_MESSAGE(state, payload) {
      state.postMessagePayload = { ...payload, _ts: Date.now() }
    },
    SET_LOCAL_DEMO_MODE(state, val) {
      state.localDemoMode = val
    },
    // 智能体首页跳转发送：暂存/清空待发送附件
    SET_PENDING_FILES(state, files) {
      state.pendingFiles = Array.isArray(files) ? files : []
    },
    SET_CEO_DEMO(state, val) {
      state.isCeoDemo = val
    },
    SET_GC(state, val) {
      state.isGc = val
    },
    SET_LQZ(state, val) {
      state.isLqz = val
    },
    SET_SESSIONS_LOADING(state, val) {
      state.sessionsLoading = val
    },
    SET_SESSIONS_LOCKED(state, val) {
      state.sessionsLocked = val
    },

    /**
     * AGENT_MUTATION — routes WS commits to the correct agent's agentData,
     * and syncs to flat state when the target agent is the current agent.
     */
    AGENT_MUTATION(
      state,
      { agentName, agentId, mutation, payload, _routingActive, _sessionKey }
    ) {
      const effectiveId = agentId || getAgentIdFromName(agentName) || agentName
      if (!effectiveId) return
      initAgentData(state, effectiveId)
      const ad = state.agentData[effectiveId]
      // _routingActive 时说明正在处理非当前会话的事件，不同步到 flat state
      // _sessionKey 明确指向其它 agent 的会话时（如切换后旧 agent 的流式/回包），同样不同步 flat state，
      // 避免旧会话内容写入新打开页面的会话
      let sessionOwnsAgent = ''
      if (_sessionKey) {
        const m = String(_sessionKey).match(/^agent:([^:]+):/)
        if (m) sessionOwnsAgent = m[1]
      }
      // Content/streaming mutations without a sessionKey while the current agent has no
      // session yet (e.g. a freshly opened new-chat home page) cannot belong to the
      // current agent - they are residual deltas from a previous agent => never sync to
      // flat state, to prevent the previous agent's output leaking into the new page.
      // Structural mutations (SET_CURRENT_SESSION_KEY / SET_SESSIONS / SET_MODELS etc.)
      // are exempt so session creation / restore flows are never blocked.
      const CONTENT_MUTATIONS = new Set([
        'ADD_MESSAGE',
        'UPDATE_LAST_MESSAGE_CONTENT',
        'UPDATE_TOOL_MESSAGE',
        'INSERT_BEFORE_STREAMING_ASSISTANT',
        'SPLIT_AT_TOOL_CALL',
        'FINALIZE_PREVIOUS_STREAMING',
        'SET_STREAMING_DONE',
        'SET_MEDIA_URLS',
        'SET_IS_STREAMING',
        'SET_CURRENT_RUN_ID',
        'SET_AGENT_ENDED',
        'SET_GLOBAL_ERROR',
        'SET_IS_ABORTING',
        'SET_IS_FINISHING',
        'SET_LAST_ASSISTANT_TEXT'
      ])
      const noSessionKey = !_sessionKey
      const currentHasSession = !!state.currentSessionKey
      // 会话级匹配：事件携带 _sessionKey 时，只有它等于当前激活会话才同步 flat state。
      // 同一 agent 下重新进入初始页新建会话后，旧会话的流式回包（_sessionKey 为旧会话）
      // 不得写入新页面的 state.messages
      const sessionMismatch =
        !!_sessionKey &&
        (!state.currentSessionKey || _sessionKey !== state.currentSessionKey)
      const isCurrent =
        !_routingActive &&
        !sessionMismatch &&
        effectiveId === state.currentAgentId &&
        (!sessionOwnsAgent || sessionOwnsAgent === state.currentAgentId) &&
        !(CONTENT_MUTATIONS.has(mutation) && noSessionKey && !currentHasSession)
      if (
        process.env.NODE_ENV !== 'production' &&
        (mutation === 'ADD_MESSAGE' ||
          mutation === 'SET_CURRENT_SESSION_KEY')
      ) {
        const dbgSk = _sessionKey || ad.currentSessionKey
        wsTrace('mutation', {
          mutation,
          isCurrent,
          effectiveId,
          routingActive: !!_routingActive,
          sessionKey: dbgSk || '',
          sessionOwnsAgent,
          sessionMismatch,
          flatCurrentAgentId: state.currentAgentId,
          flatCurrentSessionKey: state.currentSessionKey,
          flatMessageCount: state.messages.length,
          agentSessionMessageCount: (ad.sessionMessages[dbgSk] || []).length,
          messageType: mutation === 'ADD_MESSAGE' ? payload && payload.type : undefined
        })
        // 当前打开的会话内容却被写进另一个 agent 的数据桶 → 扁平 state 收不到 → 页面不渲染
        if (
          !isCurrent &&
          !_routingActive &&
          effectiveId !== state.currentAgentId &&
          _sessionKey &&
          _sessionKey === state.currentSessionKey
        ) {
          wsDrift('mutation.agentMismatch', {
            mutation,
            effectiveId,
            flatCurrentAgentId: state.currentAgentId,
            sessionKey: _sessionKey,
            agentSessionMessageCount: (ad.sessionMessages[dbgSk] || []).length
          })
        }
      }
      switch (mutation) {
        case 'SET_SOCKET_STATE':
          ad.socketState = payload
          if (isCurrent) state.socketState = payload
          break
        case 'SET_IS_HANDSHAKED':
          ad.isHandshaked = payload
          if (isCurrent) state.isHandshaked = payload
          break
        case 'SET_CURRENT_RUN_ID': {
          const sk = _sessionKey || ad.currentSessionKey
          if (sk) ad.sessionRunIds[sk] = payload
          if (isCurrent) state.currentRunId = payload
          break
        }
        case 'SET_GLOBAL_ERROR':
          ad.globalError = payload
          if (isCurrent) state.globalError = payload
          break
        case 'SET_AGENT_ENDED': {
          const sk = _sessionKey || ad.currentSessionKey
          if (sk) ad.sessionAgentEnded[sk] = payload
          if (isCurrent) state.agentEnded = payload
          break
        }
        case 'SET_MEDIA_URLS': {
          const sk = _sessionKey || ad.currentSessionKey
          if (ad.sessionMessages[sk]) {
            for (let i = ad.sessionMessages[sk].length - 1; i >= 0; i--) {
              if (
                ad.sessionMessages[sk][i].type === 'assistant' &&
                ad.sessionMessages[sk][i].isStreaming
              ) {
                ad.sessionMessages[sk][i]._mediaUrls = payload
                break
              }
            }
          }
          if (isCurrent) {
            for (let i = state.messages.length - 1; i >= 0; i--) {
              if (
                state.messages[i].type === 'assistant' &&
                state.messages[i].isStreaming
              ) {
                state.messages[i]._mediaUrls = payload
                break
              }
            }
          }
          break
        }
        case 'SET_IS_STREAMING': {
          const sk = _sessionKey || ad.currentSessionKey
          if (sk) ad.sessionIsStreaming[sk] = payload
          if (isCurrent) state.isStreaming = payload
          break
        }
        case 'SET_IS_ABORTING':
          ad.isAborting = payload
          if (isCurrent) state.isAborting = payload
          break
        case 'SET_IS_FINISHING':
          ad.isFinishing = payload
          if (isCurrent) state.isFinishing = payload
          break
        case 'ADD_MESSAGE': {
          const sk = _sessionKey || ad.currentSessionKey
          if (!ad.sessionMessages[sk]) ad.sessionMessages[sk] = []
          // 给消息打上所属会话标记，渲染层可据此过滤跨会话泄漏
          payload._sessionKey = sk
          ad.sessionMessages[sk].push(payload)
          if (isCurrent) state.messages.push(payload)
          break
        }
        case 'UPDATE_LAST_MESSAGE_CONTENT': {
          const sk = _sessionKey || ad.currentSessionKey
          if (ad.sessionMessages[sk]) {
            for (let i = ad.sessionMessages[sk].length - 1; i >= 0; i--) {
              const m = ad.sessionMessages[sk][i]
              if (m.type === 'assistant' && m.isStreaming && !m._finalized) {
                m.content = payload
                break
              }
            }
          }
          if (isCurrent) {
            for (let i = state.messages.length - 1; i >= 0; i--) {
              const m = state.messages[i]
              if (m.type === 'assistant' && m.isStreaming && !m._finalized) {
                m.content = payload
                break
              }
            }
          }
          break
        }
        case 'UPDATE_TOOL_MESSAGE': {
          const { toolCallId, updates } = payload
          const sk = _sessionKey || ad.currentSessionKey
          const applyToolUpdates = (arr) => {
            if (!Array.isArray(arr) || !arr.length) return
            let idx = -1
            let matchedById = false
            if (toolCallId) {
              idx = arr.findIndex((m) => m.toolCallId === toolCallId)
              matchedById = idx !== -1
            }
            if (idx === -1) {
              // 兜底：toolCallId 缺失/失配时，更新最近一条仍未结束的工具调用
              for (let i = arr.length - 1; i >= 0; i--) {
                if (arr[i].type === 'toolCall' && arr[i].phase !== 'result') {
                  idx = i
                  break
                }
              }
            }
            if (idx !== -1) {
              const target = arr[idx]
              if (!matchedById && updates.phase === 'result' && target.toolName) {
                // 回退更新：以目标消息自身的工具名生成完成文案，避免串名
                const merged = { ...updates }
                merged.content = `Tool ${target.toolName} completed`
                Object.assign(target, merged)
              } else {
                Object.assign(target, updates)
              }
            }
          }
          if (ad.sessionMessages[sk]) applyToolUpdates(ad.sessionMessages[sk])
          if (isCurrent) applyToolUpdates(state.messages)
          break
        }
        case 'INSERT_BEFORE_STREAMING_ASSISTANT': {
          const sk = _sessionKey || ad.currentSessionKey
          if (!ad.sessionMessages[sk]) ad.sessionMessages[sk] = []
          payload._sessionKey = sk
          let inserted = false
          for (let i = ad.sessionMessages[sk].length - 1; i >= 0; i--) {
            if (
              ad.sessionMessages[sk][i].type === 'assistant' &&
              ad.sessionMessages[sk][i].isStreaming
            ) {
              ad.sessionMessages[sk].splice(i, 0, payload)
              inserted = true
              break
            }
          }
          if (!inserted) ad.sessionMessages[sk].push(payload)
          if (isCurrent) {
            let inserted2 = false
            for (let i = state.messages.length - 1; i >= 0; i--) {
              if (
                state.messages[i].type === 'assistant' &&
                state.messages[i].isStreaming
              ) {
                state.messages.splice(i, 0, payload)
                inserted2 = true
                break
              }
            }
            if (!inserted2) state.messages.push(payload)
          }
          break
        }
        case 'SPLIT_AT_TOOL_CALL': {
          const sk = _sessionKey || ad.currentSessionKey
          if (!ad.sessionMessages[sk]) ad.sessionMessages[sk] = []
          payload._sessionKey = sk
          // 新的工具调用出现说明前面的工具已结束（串行执行）：先标记该会话仍未结束的工具
          ad.sessionMessages[sk].forEach((m) => {
            if (m.type === 'toolCall' && m.phase !== 'result') {
              m.phase = 'result'
              m.isStreaming = false
              if (m.toolName) m.content = `Tool ${m.toolName} completed`
            }
          })
          let found = false
          for (let i = ad.sessionMessages[sk].length - 1; i >= 0; i--) {
            if (
              ad.sessionMessages[sk][i].type === 'assistant' &&
              ad.sessionMessages[sk][i].isStreaming
            ) {
              ad.sessionMessages[sk][i].isStreaming = false
              ad.sessionMessages[sk][i]._finalized = true
              ad.sessionMessages[sk].splice(i + 1, 0, payload)
              found = true
              break
            }
          }
          if (!found) ad.sessionMessages[sk].push(payload)
          if (isCurrent) {
            // 去重：同 toolCallId 已存在则合并，不重复插入
            if (payload.toolCallId) {
              const dupIdx = state.messages.findIndex(
                (m) => m.toolCallId === payload.toolCallId
              )
              if (dupIdx !== -1) {
                Object.assign(state.messages[dupIdx], payload)
                break
              }
            }
            // 同样先把仍未结束的工具标记为已完成
            state.messages.forEach((m) => {
              if (m.type === 'toolCall' && m.phase !== 'result') {
                m.phase = 'result'
                m.isStreaming = false
                if (m.toolName) m.content = `Tool ${m.toolName} completed`
              }
            })
            let found2 = false
            for (let i = state.messages.length - 1; i >= 0; i--) {
              if (
                state.messages[i].type === 'assistant' &&
                state.messages[i].isStreaming
              ) {
                state.messages[i].isStreaming = false
                state.messages[i]._finalized = true
                state.messages.splice(i + 1, 0, payload)
                found2 = true
                break
              }
            }
            if (!found2) state.messages.push(payload)
          }
          break
        }
        case 'FINALIZE_PREVIOUS_STREAMING': {
          const sk = _sessionKey || ad.currentSessionKey
          if (ad.sessionMessages[sk]) {
            for (let i = ad.sessionMessages[sk].length - 1; i >= 0; i--) {
              const m = ad.sessionMessages[sk][i]
              if (m.type === 'assistant' && m.isStreaming) {
                m.isStreaming = false
                m._finalized = true
                if (m.content) m.content = stripSilentMarkers(m.content)
                break
              }
            }
          }
          if (isCurrent) {
            for (let i = state.messages.length - 1; i >= 0; i--) {
              const m = state.messages[i]
              if (m.type === 'assistant' && m.isStreaming) {
                m.isStreaming = false
                m._finalized = true
                if (m.content) m.content = stripSilentMarkers(m.content)
                break
              }
            }
          }
          break
        }
        case 'SET_STREAMING_DONE': {
          const sk = _sessionKey || ad.currentSessionKey
          if (ad.sessionMessages[sk]) {
            ad.sessionMessages[sk].forEach((m) => {
              if (m.isStreaming) {
                m.isStreaming = false
                if (m.content) m.content = stripSilentMarkers(m.content)
              }
              if (m.type === 'toolCall' && m.phase !== 'result') {
                m.phase = 'result'
                if (m.toolName) m.content = `Tool ${m.toolName} completed`
              }
            })
          }
          if (sk) ad.sessionIsStreaming[sk] = false
          ad.isAborting = false
          if (sk) ad.sessionRunIds[sk] = null
          ad.lastAssistantText = ''
          ad.agentEnded = false
          ad.isFinishing = false
          if (isCurrent) {
            state.messages.forEach((m) => {
              if (m.isStreaming) {
                m.isStreaming = false
                if (m.content) m.content = stripSilentMarkers(m.content)
              }
              if (m.type === 'toolCall' && m.phase !== 'result') {
                m.phase = 'result'
                if (m.toolName) m.content = `Tool ${m.toolName} completed`
              }
            })
            state.isStreaming = false
            state.isAborting = false
            state.currentRunId = null
            state.lastAssistantText = ''
            state.agentEnded = false
            state.isFinishing = false
          }
          break
        }
        case 'SET_SESSIONS':
          ad.sessions = payload
          if (isCurrent) state.sessions = payload
          break
        case 'SET_CURRENT_SESSION_KEY':
          ad.currentSessionKey = payload
          if (isCurrent) state.currentSessionKey = payload
          break
        case 'SET_MODELS':
          ad.models = payload.models
          ad.modelList = payload.modelList
          if (isCurrent) {
            state.models = payload.models
            state.modelList = payload.modelList
          }
          break
        case 'SET_CURRENT_MODEL':
          ad.currentModel = payload
          if (isCurrent) state.currentModel = payload
          break
        case 'SET_GATEWAY_TOKEN':
          ad.gatewayToken = payload
          if (isCurrent) state.token = payload
          break
        case 'SET_GATEWAY_URL':
          ad.gatewayUrl = payload
          if (isCurrent) state.accessUrl = payload
          break
        case 'SET_LAST_ASSISTANT_TEXT':
          ad.lastAssistantText = payload
          if (isCurrent) state.lastAssistantText = payload
          break
      }
    },

    /**
     * Save the current flat state into agentData[currentAgentId].
     * Called before switching to another agent.
     */
    SAVE_CURRENT_AGENT_STATE(state) {
      const agentId = state.currentAgentId
      if (!agentId) return
      initAgentData(state, agentId)
      const ad = state.agentData[agentId]
      ad.socketState = state.socketState
      ad.isHandshaked = state.isHandshaked
      ad.currentSessionKey = state.currentSessionKey
      ad.isAborting = state.isAborting
      ad.isFinishing = state.isFinishing
      ad.lastAssistantText = state.lastAssistantText
      ad.agentEnded = state.agentEnded
      ad.globalError = state.globalError
      if (state.currentSessionKey) {
        ad.sessionMessages[state.currentSessionKey] = JSON.parse(
          JSON.stringify(state.messages)
        )
        ad.sessionIsStreaming[state.currentSessionKey] = state.isStreaming
        ad.sessionRunIds[state.currentSessionKey] = state.currentRunId
        ad.sessionAgentEnded[state.currentSessionKey] = state.agentEnded
      }
    },

    /**
     * Restore flat state from agentData[agentId].
     * Called after switching to a new agent.
     */
    RESTORE_AGENT_STATE(state, agentId) {
      const ad = state.agentData[agentId]
      if (!ad) return
      // socketState / isHandshaked / models / modelList / currentModel 均为全局状态（共享 WS 连接、全局模型列表），不按 agent 恢复
      state.currentSessionKey = ad.currentSessionKey
      state.isAborting = ad.isAborting
      state.isFinishing = ad.isFinishing
      state.lastAssistantText = ad.lastAssistantText
      state.agentEnded = ad.agentEnded
      state.globalError = ad.globalError
      const sk = ad.currentSessionKey
      const restoredMessages =
        sk && ad.sessionMessages[sk] ? [...ad.sessionMessages[sk]] : []
      state.messages = restoredMessages
      // 只有缓存中仍存在未结束的流式消息（assistant 流式 / 未完成的工具调用）时才视为“思考中”，
      // 避免会话已结束后残留的 sessionIsStreaming 标记导致切回时一直显示“正在思考中”
      const hasLiveStreaming =
        restoredMessages.some(
          (m) => m.type === 'assistant' && m.isStreaming
        ) ||
        restoredMessages.some(
          (m) => m.type === 'toolCall' && m.phase !== 'result'
        )
      state.isStreaming =
        (sk && ad.sessionIsStreaming[sk] && hasLiveStreaming) || false
      state.currentRunId = (sk && ad.sessionRunIds[sk]) || null
    },

    SET_CURRENT_AGENT_NAME(state, payload) {
      // 支持两种形态：字符串（agentName，兼容旧调用，回退推断单值 id）或 { agentName, agentId }（显式真实 id）
      const agentName =
        typeof payload === 'string' ? payload : payload && payload.agentName
      const agentId =
        payload && typeof payload === 'object' ? payload.agentId || '' : ''
      const prevName = state.currentAgentName
      const prevId = state.currentAgentId
      state.currentAgentName = agentName
      state.currentAgentId = agentId || getAgentIdFromName(agentName) || agentName
      if (process.env.NODE_ENV !== 'production') {
        const wsAgentId = sharedWs ? sharedWs.agentId : null
        wsTrace('SET_CURRENT_AGENT_NAME', {
          agentName,
          agentId,
          resolvedAgentId: state.currentAgentId,
          prevName,
          prevId,
          wsAgentId,
          inSync: sharedWs ? wsAgentId === state.currentAgentId : null,
          socketState: state.socketState,
          isHandshaked: state.isHandshaked
        })
        // store 已切到新 agent，但共享 WS 仍指向旧 agent（抽屉只改 name 不改 WS 路由时的现场）
        if (
          sharedWs &&
          wsAgentId !== state.currentAgentId &&
          (state.socketState === 'CONNECTED' || state.isHandshaked)
        ) {
          wsDrift('storeName.wsAgentMismatch', {
            prevId,
            newId: state.currentAgentId,
            wsAgentId,
            socketState: state.socketState
          })
        }
      }
    },

    SET_CURRENT_AGENT_ID(state, agentId) {
      state.currentAgentId = agentId
    },

    /**
     * Save workbench tabs for a specific agent (数字人).
     * Called before switching between digital humans.
     */
    SAVE_WORKBENCH_TABS(state, { agentName, tabs, activeTabId }) {
      const agentId = getAgentIdFromName(agentName) || agentName
      if (!agentId) return
      initAgentData(state, agentId)
      state.agentData[agentId].workbenchTabs = JSON.parse(JSON.stringify(tabs))
      state.agentData[agentId].workbenchActiveTabId = activeTabId
    }
  },
  getters: {
    /**
     * Get saved workbench tabs for a specific agent (数字人).
     * Returns null if no tabs were saved (first visit).
     */
    getAgentWorkbenchTabs: (state) => (agentName) => {
      const agentId = getAgentIdFromName(agentName) || agentName
      const ad = state.agentData[agentId]
      if (ad && ad.workbenchTabs && ad.workbenchTabs.length > 0) {
        return {
          tabs: JSON.parse(JSON.stringify(ad.workbenchTabs)),
          activeTabId: ad.workbenchActiveTabId || 'workbench'
        }
      }
      return null
    },
    knowledgeEnabled(state) {
      return getKnowledgeSwitchEnabled(state)
    }
  },
  actions: {
    /**
     * Connect shared WebSocket (single connection for all agents — new architecture)
     */
    async connectShared(
      { commit, state, dispatch },
      { accessUrl, token, manageApiUrl } = {}
    ) {
      if (sharedWs) {
        // 共享 WS 已存在：只要底层 socket 仍处于 OPEN/CONNECTING 就直接复用，
        // 绝不重建，避免路由切换/握手窗口期反复断开重连；
        // 只有 socket 确实已关闭/不存在时才复用实例直接重连。
        const rs = sharedWs.socket ? sharedWs.socket.readyState : -1
        const socketAlive =
          rs === WebSocket.OPEN || rs === WebSocket.CONNECTING
        if (socketAlive) {
          wsTrace('connectShared.reuse', {
            readyState: rs,
            wsAgentId: sharedWs.agentId,
            storeAgentId: state.currentAgentId,
            inSync: sharedWs.agentId === state.currentAgentId
          })
          // 复用分支不会重建连接，但必须把 WS 路由同步到当前 agent，
          // 否则后续事件仍按旧 agent 判断会话归属
          if (sharedWs.agentId !== state.currentAgentId && state.currentAgentId) {
            wsDrift('connectShared.reuse.agentMismatch', {
              readyState: rs,
              wsAgentId: sharedWs.agentId,
              storeAgentId: state.currentAgentId
            })
            sharedWs.setAgentId(state.currentAgentId)
          }
          // socket 实际可用但 UI 状态异常（如曾被误报 DISCONNECTED）时修正为已连接
          if (rs === WebSocket.OPEN && state.socketState !== 'CONNECTED') {
            commit('AGENT_MUTATION', {
              agentName: state.currentAgentName || 'ceo_shu_zi_ren',
              mutation: 'SET_SOCKET_STATE',
              payload: 'CONNECTED'
            })
          }
          return
        }
        wsTrace('connectShared.reconnect', {
          readyState: rs,
          wsAgentId: sharedWs.agentId,
          storeAgentId: state.currentAgentId
        })
        sharedWs.connect(accessUrl, token)
        const curName = state.currentAgentName || 'ceo_shu_zi_ren'
        commit('AGENT_MUTATION', {
          agentName: curName,
          mutation: 'SET_GATEWAY_TOKEN',
          payload: token || ''
        })
        commit('AGENT_MUTATION', {
          agentName: curName,
          mutation: 'SET_GATEWAY_URL',
          payload: accessUrl || ''
        })
        return
      }
      const agentName = state.currentAgentName || 'ceo_shu_zi_ren'
      // 优先用 store 已解析好的 currentAgentId，避免名字反查结果与 store 不一致
      const agentId =
        state.currentAgentId || getAgentIdFromName(agentName) || agentName
      if (wsManagers[agentId]) {
        wsManagers[agentId].disconnect()
        delete wsManagers[agentId]
      }
      initAgentData(state, agentId)
      commit('AGENT_MUTATION', {
        agentName,
        mutation: 'SET_SOCKET_STATE',
        payload: 'CONNECTING'
      })

      console.log('[connectShared] 创建共享WebSocket连接:', accessUrl)
      wsTrace('connectShared.create', {
        agentName,
        agentId,
        storeAgentId: state.currentAgentId,
        inSync: agentId === state.currentAgentId,
        socketState: state.socketState
      })
      const ws = new CosmoClawWebSocket(this, agentId)
      sharedWs = ws
      wsManagers[agentId] = ws
      ws.connect(accessUrl, token)
      commit('AGENT_MUTATION', {
        agentName,
        mutation: 'SET_GATEWAY_TOKEN',
        payload: token || ''
      })
      commit('AGENT_MUTATION', {
        agentName,
        mutation: 'SET_GATEWAY_URL',
        payload: accessUrl || ''
      })

      if (state.currentAgentName === agentName) {
        commit('SET_TOKEN', token || '')
        commit('SET_ACCESS_URL', accessUrl || '')
      }

      // Fire auth POST in background
      try {
        const satoken =
          Cookies.get(
            process.env.VUE_APP_WS_TOKEN_KEY || 'tianzhi_test_agent_token'
          ) || ''
        const token = getToken()
        const wsId = localStorage.getItem('claw_workspace_id') || ''
        const WS_CLAW_ID =
          process.env.VUE_APP_WS_CLAW_ID || '2069625662527311874'
        if (satoken && wsId) {
          setAuthorizationApi({
            clawId: WS_CLAW_ID,
            authorization: satoken,
            filename: '/app/data/workspaces/' + wsId + '/.cosmo_claw_tokens',
            uuc_filename: '/app/data/workspaces/' + wsId + '/.uuc_tokens',
            uuc_token: token
          }).catch((err) => {
            console.warn('[setAuthorization] failed:', err.message)
          })
        }
      } catch (e) {
        console.error(e)
      }
    },

    /**
   * Connect a specific agent's WebSocket (incremental — doesn't disrupt other agents).
   * If a shared WS exists (new architecture), reuses it — just updates agentName.
   */
    async connectAgent(
      { commit, state, dispatch },
      { agentName, accessUrl, token, manageApiUrl }
    ) {
      const agentId = getAgentIdFromName(agentName) || agentName
      if (sharedWs) {
      // Shared connection exists — just update agentName for routing
        sharedWs.setAgentId(agentId)
        initAgentData(state, agentId)
        commit('AGENT_MUTATION', {
          agentName,
          mutation: 'SET_GATEWAY_TOKEN',
          payload: token || ''
        })
        commit('AGENT_MUTATION', {
          agentName,
          mutation: 'SET_GATEWAY_URL',
          payload: accessUrl || ''
        })
        if (state.currentAgentName === agentName) {
          commit('SET_TOKEN', token || '')
          commit('SET_ACCESS_URL', accessUrl || '')
        }
        return
      }

      if (wsManagers[agentId]) {
        wsManagers[agentId].disconnect()
        delete wsManagers[agentId]
      }
      commit('AGENT_MUTATION', {
        agentName,
        mutation: 'SET_SOCKET_STATE',
        payload: 'CONNECTING'
      })

      console.log(
        '[newWebsocket]',
        'agentName:',
        agentName,
        'accessUrl:',
        accessUrl
      )
      const ws = new CosmoClawWebSocket(this, agentId)
      wsManagers[agentId] = ws
      ws.connect(accessUrl, token)
      commit('AGENT_MUTATION', {
        agentName,
        mutation: 'SET_GATEWAY_TOKEN',
        payload: token
      })
      commit('AGENT_MUTATION', {
        agentName,
        mutation: 'SET_GATEWAY_URL',
        payload: accessUrl
      })

      if (state.currentAgentName === agentName) {
        commit('SET_TOKEN', token)
        commit('SET_ACCESS_URL', accessUrl)
      }

      // Fire auth POST in background
      try {
        const satoken =
        Cookies.get(
          process.env.VUE_APP_WS_TOKEN_KEY || 'tianzhi_test_agent_token'
        ) || ''
        const token = getToken()
        const wsId = localStorage.getItem('claw_workspace_id') || ''
        const WS_CLAW_ID = process.env.VUE_APP_WS_CLAW_ID || '2069625662527311874'
        if (satoken && wsId) {
          setAuthorizationApi({
            clawId: WS_CLAW_ID,
            authorization: satoken,
            filename: '/app/data/workspaces/' + wsId + '/.cosmo_claw_tokens',
            uuc_filename: '/app/data/workspaces/' + wsId + '/.uuc_tokens',
            uuc_token: token
          }).catch((err) => {
            console.warn('[setAuthorization] failed:', err.message)
          })
        }
      } catch (e) {
        console.error(e)
      }
    },

    /**
   * Disconnect a specific agent (or all if no agentName given).
   */
    disconnectAgent({ dispatch }, agentNameOrId) {
      dispatch('stopSessionPolling', agentNameOrId)
      if (sharedWs) {
      // Don't disconnect shared WS per-agent — use disconnect() to clean up all
        return
      }
      const agentId = getAgentIdFromName(agentNameOrId) || agentNameOrId
      if (wsManagers[agentId]) {
        wsManagers[agentId].disconnect()
        delete wsManagers[agentId]
      }
    },

    async connect(
      { commit, state, dispatch },
      { accessUrl, token, manageApiUrl, agentName }
    ) {
      if (sharedWs) return // already connected via shared WS
      const name = agentName || state.currentAgentName || 'chang_zhang_shu_zi_ren'
      return dispatch('connectAgent', {
        agentName: name,
        accessUrl,
        token,
        manageApiUrl
      })
    },

    disconnect({ dispatch }, agentName) {
      if (sharedWs) {
        wsTrace('disconnectShared', {
          agentName,
          wsAgentId: sharedWs.agentId
        })
        sharedWs.disconnect()
        sharedWs = null
        Object.keys(wsManagers).forEach((id) => {
          delete wsManagers[id]
        })
        return
      }
      if (agentName) {
        dispatch('disconnectAgent', agentName)
      } else {
        Object.keys(wsManagers).forEach((name) => {
          dispatch('disconnectAgent', name)
        })
      }
    },

    /**
   * Switch to a different agent (save current, restore target, no WS disconnect).
   */
    switchAgent({ commit, state, dispatch }, payload) {
      // ???????
      // 1) { agentName, agentId } ?? ?????? agentId????? CEO ??/??/?????????
      // 2) ??? agentName ?? ????????? agentId?
      // 3) ?????? agentId ?? ? sessionKey ???? id?? analysisFlow??? id ?? agentName
      // ???Vuex dispatch(type, payload, options) ??????????????
      // dispatch('cosmoClaw/switchAgent', name, id) ?????????? options ???
      // ??????????????????? agentId?????????
      let targetAgentName = ''
      let targetAgentId = ''
      if (payload && typeof payload === 'object') {
        targetAgentName = payload.agentName || ''
        targetAgentId = payload.agentId || ''
      } else if (typeof payload === 'string') {
        if (/^\d+$/.test(payload)) {
          targetAgentId = payload
          targetAgentName = getAgentNameFromId(payload) || ''
        } else {
          targetAgentName = payload
        }
      }
      const agentName =
        targetAgentName ||
        getAgentNameFromId(targetAgentId) ||
        targetAgentId ||
        state.currentAgentName ||
        ''
      const agentId = targetAgentId || getAgentIdFromName(agentName) || agentName
      wsTrace('switchAgent', {
        payload,
        agentName,
        agentId,
        storeAgentId: state.currentAgentId,
        wsAgentId: sharedWs ? sharedWs.agentId : null
      })
      // 同 internalName 但不同实例（真实 id 不同，如 CEO 高管/普通/演示）也要切换
      if (agentName === state.currentAgentName && agentId === state.currentAgentId) {
        // store 状态未变，但共享 WS 可能仍指向别的 agent（其它入口只改了 store 时），
        // 这里补一次同步，避免命中早退后永远无法自愈
        if (sharedWs && sharedWs.agentId !== agentId) {
          wsDrift('switchAgent.earlyReturn.wsAgentMismatch', {
            agentName,
            agentId,
            wsAgentId: sharedWs.agentId
          })
          sharedWs.setAgentId(agentId)
        } else {
          wsTrace('switchAgent.skip', { agentName, agentId })
        }
        return
      }
      commit('SAVE_CURRENT_AGENT_STATE')
      commit('SET_CURRENT_AGENT_NAME', { agentName, agentId })
      commit('RESTORE_AGENT_STATE', agentId)
      // Switch agent: clear knowledge id and load new agent knowledge; switch state is per-session
      commit('SET_KNOWLEDGE_ID', '')
      dispatch('loadAgentKnowledge', agentId)
      dispatch('loadModels', { _agentId: agentId })
      dispatch('mergeAllSessions')
      // 共享 WS 模式下，更新 WS 路由到当前 agent
      if (sharedWs) {
        sharedWs.setAgentId(agentId)
        wsTrace('switchAgent.setAgentId', {
          agentId,
          storeAgentId: state.currentAgentId,
          wsAgentId: sharedWs.agentId
        })
      }
    },

    /**
     * 加载当前数字人对应的知识库 id（请求 /sphere-admin/api/v1/agent/knowledge）
     */
    async loadAgentKnowledge({ commit, state }, agentId) {
      const id =
        agentId ||
        state.currentAgentId ||
        getAgentIdFromName(state.currentAgentName) ||
        state.currentAgentName
      if (!id) return ''
      try {
        const res = await getKnowledgeClientPageApi({ agentId: id })
        // 响应返回期间可能已切换数字人，旧请求不得覆盖新数字人的知识库
        const currentId =
          state.currentAgentId ||
          getAgentIdFromName(state.currentAgentName) ||
          state.currentAgentName
        if (currentId && currentId !== id) return ''
        const knowledgeId = extractKnowledgeIdFromRes(res)
        commit('SET_KNOWLEDGE_ID', knowledgeId)
        return knowledgeId
      } catch (e) {
        console.warn('[cosmoClaw] 获取数字人知识库失败:', e)
        const currentId =
          state.currentAgentId ||
          getAgentIdFromName(state.currentAgentName) ||
          state.currentAgentName
        if (currentId && currentId !== id) return ''
        commit('SET_KNOWLEDGE_ID', '')
        return ''
      }
    },

    /**
   * Merge all agents' sessions into a unified state.sessions list.
   */
    mergeAllSessions({ state, commit }) {
    // 先按 agentData 收集所有 session（去重）
      const sessionMap = new Map()
      Object.entries(state.agentData).forEach(([agentId, ad]) => {
        const agentName = getAgentNameFromId(agentId) || agentId
        if (ad.sessions && ad.sessions.length > 0) {
          ad.sessions.forEach((s) => {
            const globalKey = `${agentId}:${s.key}`
            if (sessionMap.has(globalKey)) return
            sessionMap.set(globalKey, { ...s, agentName, agentId, globalKey })
          })
        }
      })
      // 按服务端原始顺序排列
      const ordered = []
      const added = new Set()
      state._sessionOrder.forEach((key) => {
        const globalKey = `${
          key.startsWith('agent:')
            ? key.match(/^agent:([^:]+):/)?.[1] || 'main'
            : 'main'
        }:${key}`
        let session = sessionMap.get(globalKey)
        if (!session) session = sessionMap.get(key)
        if (session && !added.has(session.globalKey)) {
          added.add(session.globalKey)
          ordered.push(session)
        }
      })
      // 未在服务端原始顺序中的 session（如新建的 pending 会话）排在前面
      const pending = []
      sessionMap.forEach((session) => {
        if (!added.has(session.globalKey)) {
          added.add(session.globalKey)
          pending.push(session)
        }
      })
      commit('SET_SESSIONS', [...pending, ...ordered])
    },

    async sendMessage(
      { state, commit, dispatch },
      { content, sessionKey, files, displayContent }
    ) {
      const agentName = state.currentAgentName
      const agentId = state.currentAgentId || getAgentIdFromName(agentName) || agentName
      const ws = getEffectiveWs(agentId)
      // 共享 WS 的 agentId 是事件路由依据。抽屉等入口只改了 store 的 currentAgentId，
      // 未同步 WS；若此时发送，回包会按旧 agent 的会话判断被误判为「其它会话」，
      // 只写入 agentData 而不进扁平 messages，表现为「有数据但页面不渲染」。
      if (ws && ws === sharedWs && ws.agentId !== agentId) {
        ws.setAgentId(agentId)
      }
      if (state.localDemoMode) {
        if (typeof document !== 'undefined') {
          document.dispatchEvent(
            new CustomEvent('cosmo-claw-local-demo-message', {
              detail: { content, sessionKey, files }
            })
          )
        }
        return
      }
      if (!ws || !state.isHandshaked) {
        console.warn(
          '[cosmoClaw] sendMessage blocked: wsManager=',
          !!ws,
          'isHandshaked=',
          state.isHandshaked
        )
        return
      }
      const effectiveSessionKey = sessionKey || state.currentSessionKey
      if (!effectiveSessionKey) {
        console.warn('[cosmoClaw] sendMessage blocked: no effectiveSessionKey')
        return
      }
      if (process.env.NODE_ENV !== 'production') {
        wsTrace('send', {
          agentName,
          agentId,
          wsAgentId: ws.agentId,
          inSync: ws.agentId === agentId,
          storeAgentId: state.currentAgentId,
          effectiveSessionKey,
          storeCurrentSessionKey: state.currentSessionKey,
          isHandshaked: state.isHandshaked
        })
        // 发送时 WS 仍指向别的 agent：回包会被写进别的 agent 数据桶，当前页面收不到
        if (ws.agentId !== agentId) {
          wsDrift('send.wsAgentMismatch', {
            agentName,
            agentId,
            wsAgentId: ws.agentId,
            effectiveSessionKey
          })
        }
      }

      // 用户消息：席位校验不通过时也照常插入，仅终止后续发送
      const fileUrls = (files || []).filter((f) => f.url).map((f) => f.url)
      const fullMessage =
      fileUrls.length > 0 ? fileUrls.join('\n') + '\n' + content : content
      const userMsg = {
        id: generateUUID(),
        type: 'user',
        content: displayContent || content,
        timestamp: Date.now(),
        isHistory: false,
        richHtml: !!displayContent,
        attachments:
        files && files.length > 0
          ? files.map((f) => ({ name: f.name, url: f.url }))
          : undefined
      }
      const agentMutation = (mutation, payload) =>
        commit('AGENT_MUTATION', {
          agentId,
          mutation,
          payload,
          _sessionKey: effectiveSessionKey
        })

      // 统一席位校验：数字人 + 智能体发送真实消息前均校验
      // - 接口异常：toast 提示并拦截（不插任何消息）
      // - 校验未通过：插入用户消息 + 席位上限提示卡片，不继续发送
      // - 校验通过：继续正常流程
      try {
        const seatRes = await validateSphereUserSeat()
        const seatData = (seatRes && seatRes.data) || seatRes || {}
        if (!isSphereSeatValid(seatData)) {
          const total = Number(seatData.userNum) || 0
          // 当前席位固定展示席位上限（已满），可用席位为 0
          const used = total
          const available = 0
          const seatLimitMsg = {
            id: generateUUID(),
            type: 'assistant',
            content: JSON.stringify({
              seatLimit: true,
              used,
              available,
              total,
              message: seatData.message || ''
            }),
            timestamp: Date.now(),
            isHistory: false
          }
          agentMutation('FINALIZE_PREVIOUS_STREAMING', undefined)
          agentMutation('ADD_MESSAGE', userMsg)
          agentMutation('ADD_MESSAGE', seatLimitMsg)
          return
        }
      } catch (e) {
        console.warn('[cosmoClaw] sendMessage 席位校验失败:', e)
        Message.error(
          '席位校验失败：' + ((e && e.message) || '请稍后重试')
        )
        return
      }

      // 同步 currentSessionKey（flat + agentData），确保 WebSocket 回包时 sessionKey 匹配
      commit('AGENT_MUTATION', {
        agentId,
        mutation: 'SET_CURRENT_SESSION_KEY',
        payload: effectiveSessionKey
      })

      const runId = generateUUID()
      // 记录最近一次发起发送的会话与 runId，供 WS 事件归属推断（runId 未追踪 / 归属缺失时避免串页）
      if (ws && typeof ws.recordSend === 'function') {
        ws.recordSend(effectiveSessionKey, agentId, runId)
      }
      // 同步到 agentData 会话缓存，避免切换会话后刚发送的用户消息丢失
      agentMutation('FINALIZE_PREVIOUS_STREAMING', undefined)
      agentMutation('ADD_MESSAGE', userMsg)
      agentMutation('SET_IS_STREAMING', true)
      commit('SET_SESSION_IS_LIVE', true)
      agentMutation('SET_CURRENT_RUN_ID', runId)
      agentMutation('SET_AGENT_ENDED', false)
      agentMutation('SET_GLOBAL_ERROR', null)
      commit('SET_TASK_PROGRESS_VISIBLE', false)
      commit('SET_TASK_PROGRESS_LIST', [])
      commit('SET_DRAWER_VISIBLE', false)
      commit('SET_DRAWER_CONFIG', {})

      const session = state.sessions.find((s) => s.key === effectiveSessionKey)
      if (session && session.isPending) {
        let serverLabels = []
        try {
          const sessionsPayload = await ws.sendRequest('sessions.list', {
            includeGlobal: true,
            includeUnknown: true
          })
          if (sessionsPayload && sessionsPayload.sessions) {
            serverLabels = sessionsPayload.sessions.map((s) => s.label)
          }
        } catch (e) {
          console.error('获取会话列表失败:', e)
        }
        const allLabels = [...serverLabels, ...state.sessions.map((s) => s.label)]
        let label = content.substring(0, 20)
        let counter = 1
        while (allLabels.includes(label)) {
          label = `${content.substring(0, 20)}(${counter})`
          counter++
        }
        try {
          await Promise.race([
            ws.sendRequest('sessions.patch', {
              key: effectiveSessionKey,
              label,
              model: session.model || undefined
            }),
            new Promise((resolve) => setTimeout(resolve, 2000))
          ])
        } catch (e) {
          console.error('创建会话失败:', e)
        }
        // 在包含该 session 的 agentData 中更新 pending 状态（可能在 'main' 或其他 agent 下）
        for (const adName of Object.keys(state.agentData)) {
          const ad = state.agentData[adName]
          if (
            ad.sessions &&
          ad.sessions.some((s) => s.key === effectiveSessionKey)
          ) {
            const updated = ad.sessions.map((s) =>
              s.key === effectiveSessionKey
                ? { ...s, label, isPending: false }
                : s
            )
            commit('AGENT_MUTATION', {
              agentName: adName,
              mutation: 'SET_SESSIONS',
              payload: updated
            })
            break
          }
        }
        dispatch('mergeAllSessions')
      }

      // 拦截 [mock:xxx] 标记：不走 WebSocket，触发前端 mock 流程
      if (content && typeof content === 'string') {
        var mockMatch = content.match(/^\[mock:([^\]]+)\]/)
        if (mockMatch) {
          if (typeof document !== 'undefined') {
            document.dispatchEvent(new CustomEvent('cosmo-claw-mock-trigger', {
              detail: { scenario: mockMatch[1], content }
            }))
          }
          return
        }
      }

      const chatParams = {
        message: fullMessage,
        sessionKey: effectiveSessionKey,
        idempotencyKey: runId,
        deliver: false,
        ...resolveChatModelParams(state, effectiveSessionKey),
        ...resolveKnowledgeParams(state)
      }
      ws.sendRequest('chat.send', chatParams)
        .then((res) => {})
        .catch((err) => {
          console.error('[cosmoClaw] chat.send error:', err)
        })
    },
    sendSilentMessage({ state, commit }, { content, sessionKey }) {
      const agentId =
        state.currentAgentId ||
        getAgentIdFromName(state.currentAgentName) ||
        state.currentAgentName
      const ws = getEffectiveWs(agentId)
      if (!ws || !state.isHandshaked) {
        console.warn('[cosmoClaw] sendSilentMessage blocked: not connected')
        return
      }
      const effectiveSessionKey = sessionKey || state.currentSessionKey
      if (!effectiveSessionKey) return
      commit('AGENT_MUTATION', {
        agentId,
        mutation: 'FINALIZE_PREVIOUS_STREAMING',
        payload: undefined,
        _sessionKey: effectiveSessionKey
      })
      const idempotencyKey = generateUUID()
      // 记录最近一次发起发送的会话与 runId，供 WS 事件归属推断（runId 未追踪 / 归属缺失时避免串页）
      if (ws && typeof ws.recordSend === 'function') {
        ws.recordSend(effectiveSessionKey, agentId, idempotencyKey)
      }
      ws.sendRequest('chat.send', {
        message: content,
        sessionKey: effectiveSessionKey,
        idempotencyKey,
        deliver: false,
        ...resolveChatModelParams(state, effectiveSessionKey),
        ...resolveKnowledgeParams(state)
      }).catch((err) => {
        console.error('[cosmoClaw] sendSilentMessage error:', err)
      })
    },

    async sendHideMessage(
      { state, commit, dispatch },
      { content, sessionKey, files }
    ) {
      const agentId =
        state.currentAgentId ||
        getAgentIdFromName(state.currentAgentName) ||
        state.currentAgentName
      const ws = getEffectiveWs(agentId)
      if (!ws || !state.isHandshaked) {
        console.warn('[cosmoClaw] sendHideMessage blocked: no ws connection')
        return
      }
      const effectiveSessionKey = sessionKey || state.currentSessionKey
      if (!effectiveSessionKey) {
        console.warn('[cosmoClaw] sendHideMessage blocked: no sessionKey')
        return
      }
      const runId = generateUUID()
      // 记录最近一次发起发送的会话与 runId，供 WS 事件归属推断（runId 未追踪 / 归属缺失时避免串页）
      if (ws && typeof ws.recordSend === 'function') {
        ws.recordSend(effectiveSessionKey, agentId, runId)
      }
      const agentMutation = (mutation, payload) =>
        commit('AGENT_MUTATION', {
          agentId,
          mutation,
          payload,
          _sessionKey: effectiveSessionKey
        })
      // 同步到 agentData 会话缓存，避免切换会话后刚结束的轮次状态/消息丢失
      agentMutation('FINALIZE_PREVIOUS_STREAMING', undefined)
      agentMutation('SET_IS_STREAMING', true)
      agentMutation('SET_CURRENT_RUN_ID', runId)
      agentMutation('SET_AGENT_ENDED', false)
      agentMutation('SET_GLOBAL_ERROR', null)
      commit('SET_TASK_PROGRESS_VISIBLE', false)
      commit('SET_TASK_PROGRESS_LIST', [])
      commit('SET_DRAWER_VISIBLE', false)
      commit('SET_DRAWER_CONFIG', {})
      const chatParams = {
        message: content,
        sessionKey: effectiveSessionKey,
        idempotencyKey: runId,
        deliver: false,
        ...resolveChatModelParams(state, effectiveSessionKey),
        ...resolveKnowledgeParams(state)
      }
      ws.sendRequest('chat.send', chatParams)
        .then((res) => {
          console.log('[cosmoClaw] sendHideMessage response:', res)
        })
        .catch((err) => {
          console.error('[cosmoClaw] sendHideMessage error:', err)
        })
    },

    abortConversation({ state, commit }, { agentId, sessionKey, runId } = {}) {
      const targetAgentId =
        agentId ||
        state.currentAgentId ||
        getAgentIdFromName(state.currentAgentName) ||
        state.currentAgentName
      const ws = getEffectiveWs(targetAgentId)
      const targetSessionKey = sessionKey || state.currentSessionKey
      const ad = state.agentData[targetAgentId]
      const targetRunId =
        runId ||
        (ad && ad.sessionRunIds && ad.sessionRunIds[targetSessionKey]) ||
        state.currentRunId
      if (!ws || !targetRunId) return Promise.resolve()
      commit('SET_IS_ABORTING', true)
      // 记录已中止的 run 并重置该会话的流式文本基准：
      // 中止后会在原会话继续提问，旧 run 的迟到事件必须丢弃，否则会污染新一轮回答
      if (typeof ws.markRunAborted === 'function') ws.markRunAborted(targetRunId)
      if (typeof ws.resetSessionStreamState === 'function') {
        ws.resetSessionStreamState(targetSessionKey)
      }
      // 超时兜底：服务端未回包时不阻塞调用方（删除会话、新建会话等后续流程）
      return Promise.race([
        ws.sendRequest('chat.abort', {
          sessionKey: targetSessionKey,
          runId: targetRunId
        }),
        new Promise((resolve) => setTimeout(() => resolve(null), 2000))
      ]).catch((err) => {
        console.warn('[cosmoClaw] abortConversation failed:', err)
        return null
      })
    },

    updateModel({ state, commit, dispatch }, { modelId }) {
      commit('AGENT_MUTATION', {
        agentId: state.currentAgentId,
        mutation: 'SET_CURRENT_MODEL',
        payload: modelId
      })
      const agentId =
        state.currentAgentId ||
        getAgentIdFromName(state.currentAgentName) ||
        state.currentAgentName
      // 同步本地当前会话的 model，避免切换后立即发送时 chat.send 仍取到切换前的 session.model。
      // 扫描所有 agentData（会话可能记录在其它 agentId 下），确保本地会话模型一定被更新
      const sessionKey = state.currentSessionKey
      let patchedSession = false
      if (sessionKey) {
        Object.entries(state.agentData).forEach(([adKey, ad]) => {
          if (!ad.sessions || !ad.sessions.some((s) => s.key === sessionKey)) return
          const sessions = ad.sessions.map((s) =>
            s.key === sessionKey ? { ...s, model: modelId } : s
          )
          commit('AGENT_MUTATION', {
            agentId: adKey,
            mutation: 'SET_SESSIONS',
            payload: sessions
          })
          patchedSession = true
        })
        if (patchedSession) dispatch('mergeAllSessions')
      }
      const ws = getEffectiveWs(agentId)
      if (ws && sessionKey && state.isHandshaked) {
        Promise.race([
          ws.sendRequest('sessions.patch', {
            key: sessionKey,
            model: modelId
          }),
          new Promise((resolve) => setTimeout(resolve, 2000))
        ]).catch((e) => {
          console.error('[cosmoClaw] 模型切换失败:', e)
          Message.error('模型切换失败')
        })
      }
    },

    loadSessions(
      { state, commit, dispatch },
      { lock = false, _agentName, _agentId } = {}
    ) {
      const targetName = _agentName || state.currentAgentName
      const agentId =
        _agentId ||
        getAgentIdFromName(_agentName) ||
        state.currentAgentId ||
        getAgentIdFromName(targetName) ||
        targetName
      if (!agentId) return
      const ws = getEffectiveWs(agentId)
      const ad = state.agentData[agentId]
      if (!ws || !ad || !ad.isHandshaked) return
      commit('SET_SESSIONS_LOADING', true)
      if (lock) commit('SET_SESSIONS_LOCKED', true)
      ws.sendRequest('sessions.list', {
        includeGlobal: true,
        includeUnknown: true
      })
        .then((payload) => {
          if (payload && payload.sessions) {
            const serverSessions = payload.sessions.filter(
              (s) => s.key !== 'agent:main:main'
            )
            // 按 sessionKey 中的 agentId 分发到对应的 agentData
            const sessionsByAgentId = {}
            serverSessions.forEach((s) => {
              const match = s.key.match(/^agent:([^:]+):/)
              const sAgentId = match ? match[1] : 'main'
              if (!sessionsByAgentId[sAgentId]) sessionsByAgentId[sAgentId] = []
              sessionsByAgentId[sAgentId].push(s)
            })
            Object.entries(sessionsByAgentId).forEach(([sAgentId, sessions]) => {
              initAgentData(state, sAgentId)
              const targetAd = state.agentData[sAgentId]
              const prevSessions = targetAd.sessions || []
              const pendingSession = prevSessions.find((s) => s.isPending)
              let finalSessions = sessions
              if (
                pendingSession &&
              !sessions.some((s) => s.key === pendingSession.key)
              ) {
                finalSessions = [pendingSession, ...sessions]
              }
              // 保留本地已记录的会话 model：sessions.list 可能不返回（或返回过期）model 字段，
              // 直接整体覆盖会在 sessions.changed / 30s 轮询触发时把用户刚切换的模型清掉，
              // 导致 chat.send 回退到默认模型。本地有记录时以本地为准。
              finalSessions = finalSessions.map((s) => {
                const local = prevSessions.find((ls) => ls.key === s.key)
                return local && local.model ? { ...s, model: local.model } : s
              })
              commit('AGENT_MUTATION', {
                agentId: sAgentId,
                mutation: 'SET_SESSIONS',
                payload: finalSessions
              })
            })
            // 也更新触发该请求的 agent 的 model：仅当当前会话没有本地记录的模型时采用服务端默认值，
            // 避免轮询 / sessions.changed 把用户显式切换的模型覆盖回默认
            const currentSession = (state.sessions || []).find(
              (s) => s.key === state.currentSessionKey
            )
            if (
              payload &&
              payload.defaults &&
              payload.defaults.model &&
              !(currentSession && currentSession.model)
            ) {
              commit('AGENT_MUTATION', {
                agentId,
                mutation: 'SET_CURRENT_MODEL',
                payload: payload.defaults.model
              })
            }
            // 记录服务端返回的原始 session 顺序
            commit(
              'SET_SESSION_ORDER',
              serverSessions.map((s) => s.key)
            )
            dispatch('mergeAllSessions')
          }
        })
        .catch((err) => {
          console.error(`获取 sessions 失败 [${agentId}]:`, err)
        })
        .finally(() => {
          commit('SET_SESSIONS_LOADING', false)
          if (lock) commit('SET_SESSIONS_LOCKED', false)
        })
    },

    startSessionPolling({ dispatch }, agentName) {
      const name =
      typeof agentName === 'string'
        ? agentName
        : (agentName && agentName._agentName) || 'default'
      dispatch('stopSessionPolling', name)
      if (sessionPollTimers[name]) {
        clearInterval(sessionPollTimers[name])
      }
      sessionPollTimers[name] = setInterval(() => {
        dispatch('loadSessions', { _agentName: name })
      }, 30000)
    },

    stopSessionPolling({ state }, agentName) {
      const name =
      typeof agentName === 'string'
        ? agentName
        : (agentName && agentName._agentName) || undefined
      if (name) {
        if (sessionPollTimers[name]) {
          clearInterval(sessionPollTimers[name])
          delete sessionPollTimers[name]
        }
      } else {
        Object.keys(sessionPollTimers).forEach((timerKey) => {
          clearInterval(sessionPollTimers[timerKey])
          delete sessionPollTimers[timerKey]
        })
      }
    },

    loadHistory(
      { state, commit, dispatch },
      { _agentName, _agentId, sessionKey } = {}
    ) {
      if (state.localDemoMode) return
      const agentId =
      _agentId ||
      getAgentIdFromName(_agentName) ||
      state.currentAgentId ||
      getAgentIdFromName(state.currentAgentName) ||
      state.currentAgentName
      const ws = getEffectiveWs(agentId)
      const effectiveSessionKey = sessionKey || state.currentSessionKey
      if (!ws || !effectiveSessionKey) return
      // 在途去重：同 agentId + sessionKey 的历史请求尚未返回时，复用同一个 Promise，
      // 不再发送第二个 chat.history（响应解析/提交也只执行一次）
      const pendingKey = `${agentId}::${effectiveSessionKey}`
      if (historyPending.has(pendingKey)) {
        return historyPending.get(pendingKey)
      }
      const requestPromise = ws.sendRequest('chat.history', {
        sessionKey: effectiveSessionKey,
        limit: 200
      })
        .then((payload) => {
          if (payload && payload.messages) {
            const messages = parseHistoryMessages(payload.messages)
            const ad = state.agentData[agentId]
            if (ad) {
            // 如果该 session 已有流式输出或本地即时消息，不覆盖（服务端历史快照可能未包含最新一轮）
              const existing = ad.sessionMessages[effectiveSessionKey]
              const hasStreaming = existing && existing.some((m) => m.isStreaming)
              const hasLocalLive =
                existing && existing.some((m) => !m.isHistory)
              if (!hasStreaming && !hasLocalLive) {
                ad.sessionMessages[effectiveSessionKey] = messages
                if (agentId === state.currentAgentId) {
                  commit('SET_MESSAGES', messages)
                }
              }
            }
            const rawAssistantMessages = payload.messages
              .filter((m) => m.role === 'assistant')
              .map((m) => ({
                type: 'assistant',
                content: Array.isArray(m.content)
                  ? m.content
                    .filter((c) => c.type === 'text' && c.text)
                    .map((c) => c.text)
                    .join('')
                  : typeof m.content === 'string'
                    ? m.content
                    : ''
              }))
            const iframeItems = extractLastIframeItems(rawAssistantMessages)
            if (iframeItems) {
              commit('SET_IFRAME_ITEMS', iframeItems)
              commit('SET_IFRAME_VISIBLE', true)
              commit('SET_IFRAME_ACTIVE_INDEX', 0)
            }
          }
        })
        .catch((err) => {
          console.error(`加载历史消息失败 [${agentId}]:`, err)
        })
        .finally(() => {
          // 请求结束（成功/失败）即释放：失败后允许后续切换重新拉取，避免永久锁
          historyPending.delete(pendingKey)
        })
      historyPending.set(pendingKey, requestPromise)
      return requestPromise
    },

    loadModels({ state, commit }, { _agentName, _agentId } = {}) {
      // 模型列表只允许用真实数字人 id（agentList 中的数字 id）请求：
      // 没有数字人（agentList 为空）时，不再用内部名(如 ceo_shu_zi_ren)兜底，
      // 避免拼出 /agent-api/claw/api/v1/models/agent/ceo_shu_zi_ren 这类无效请求
      const agentId =
        _agentId ||
        getAgentIdFromName(_agentName) ||
        state.currentAgentId ||
        getAgentIdFromName(state.currentAgentName) ||
        ''
      // 解析不到真实 id（无数字人）时直接跳过，不调接口
      if (!agentId || !/^\d+$/.test(String(agentId))) return
      // 模型列表改为 HTTP 接口：/agent-api/claw/api/v1/models/agent/{agentId}（替代原 models.list websocket 调用）
      getAgentModelsApi(agentId)
        .then((res) => {
          const data = (res && res.data) || []
          const modelList = []
          ;(Array.isArray(data) ? data : []).forEach((provider) => {
            const providerName = (provider && provider.name) || ''
            const extraModels = Array.isArray(provider && provider.extraModels)
              ? provider.extraModels
              : []
            extraModels.forEach((m) => {
              modelList.push({
                id: (m && (m.id || m.name)) || '',
                name: (m && (m.name || m.id)) || '',
                provider: providerName,
                contextWindow: m && m.contextWindow,
                reasoning: m && m.reasoning,
                input: m && m.input
              })
            })
          })
          const models = modelList.map((m) => m.id).filter(Boolean)
          commit('AGENT_MUTATION', {
            agentId,
            mutation: 'SET_MODELS',
            payload: { models, modelList }
          })
          // 历史会话可能采用了新模型列表中已不存在的模型：自动切换到新列表第一个模型
          const ad = state.agentData[agentId]
          const prevModel = (ad && ad.currentModel) || state.currentModel || ''
          if (models.length > 0 && prevModel && models.indexOf(prevModel) === -1) {
            commit('AGENT_MUTATION', {
              agentId,
              mutation: 'SET_CURRENT_MODEL',
              payload: models[0]
            })
          }
        })
        .catch((err) => {
          console.error(`获取模型列表失败 [${agentId}]:`, err)
        })
    },

    createNewSession(
      { state, commit, dispatch },
      { agentName: paramAgentName, agentId: paramAgentId, forceNew } = {}
    ) {
      const agentName =
      paramAgentName || state.currentAgentName || 'chang_zhang_shu_zi_ren'
      // agentId 优先显式传入；未传且创建对象就是当前数字人时，直接用当前真实 agentId
      // （同 internalName 多实例如 CEO 高管/普通/演示各有独立 id，AGENT_ID_MAP 单值兜底会串号）
      const agentId =
        paramAgentId ||
        (agentName === state.currentAgentName ? state.currentAgentId : '') ||
        getAgentIdFromName(agentName) ||
        agentName

      const ad = state.agentData[agentId]
      const existingPending =
      !forceNew && ad ? (ad.sessions || []).find((s) => s.isPending) : null
      // 仅在 pending 会话仍为空且未在流式输出时才复用（如反复进入初始页）；
      // 已发送过消息/正在输出的 pending 会话不能复用，否则重新进入初始页会把
      // 旧会话的流式内容渲染到新页面
      const pendingMessages =
        existingPending && ad.sessionMessages
          ? ad.sessionMessages[existingPending.key] || []
          : []
      const pendingIsLive =
        !!existingPending &&
        (pendingMessages.length > 0 ||
          (ad.sessionIsStreaming &&
            ad.sessionIsStreaming[existingPending.key]) ||
          // flat state 兜底：当前 agent 正在展示该 pending 会话且有消息（如发送后尚未写入 agentData 的边界情况），
          // 视为活跃会话不复用，避免旧会话内容被渲染到新打开的初始页
          (state.currentAgentId === agentId &&
            state.currentSessionKey === existingPending.key &&
            state.messages.length > 0))
      if (existingPending && !pendingIsLive) {
        commit('AGENT_MUTATION', {
          agentId,
          mutation: 'SET_CURRENT_SESSION_KEY',
          payload: existingPending.key
        })
        commit('AGENT_MUTATION', {
          agentId,
          mutation: 'SET_SESSIONS',
          payload: (ad.sessions || []).map((s) => s)
        })
        commit('SET_MESSAGES', [])
        commit('SET_IS_STREAMING', false)
        commit('SET_CURRENT_RUN_ID', null)
        dispatch('mergeAllSessions')
        commit('MIGRATE_KNOWLEDGE_SWITCH_DRAFT')
        return existingPending.key
      }

      const sessionId = generateUUID()
      const userId = this.getters.userInfo?.userCode || ''
      const prefix = userId ? `${userId}|!` : ''
      // sessionKey 前缀必须用真实 agentId：多实例（如 CEO 高管/普通/演示）各有独立 id，
      // 用单值 AGENT_ID_MAP 会导致不同实例共用同一会话前缀、状态互相串扰
      const sessionKey = `agent:${agentId}:${prefix}${sessionId}`
      // 会话记录当前生效模型：显式选择优先，未选择时与输入框默认展示一致（模型列表第一个）
      const modelList = state.modelList || []
      const fallbackModelId = modelList.length > 0 ? modelList[0].id : ''
      let modelParam = state.currentModel || fallbackModelId || ''
      // 历史会话采用的模型可能已不在新模型列表中：自动切换到模型列表第一个
      if (modelParam && modelList.length > 0 && !modelList.some((m) => m.id === modelParam)) {
        modelParam = fallbackModelId || ''
      }

      const newSession = {
        key: sessionKey,
        label: '新会话',
        model: modelParam,
        isPending: true,
        createdAt: Date.now()
      }
      const curSessions = ad ? ad.sessions || [] : []
      commit('AGENT_MUTATION', {
        agentId,
        mutation: 'SET_SESSIONS',
        payload: [newSession, ...curSessions]
      })
      commit('AGENT_MUTATION', {
        agentId,
        mutation: 'SET_CURRENT_SESSION_KEY',
        payload: sessionKey
      })
      commit('SET_MESSAGES', [])
      commit('SET_IS_STREAMING', false)
      commit('SET_CURRENT_RUN_ID', null)
      dispatch('mergeAllSessions')
      commit('MIGRATE_KNOWLEDGE_SWITCH_DRAFT')
      return sessionKey
    },

    switchSession({ state, commit, dispatch }, { sessionKey, agentName, agentId, forceReload } = {}) {
      const targetAgentName = agentName || state.currentAgentName
      const targetAgentId = agentId || getAgentIdFromName(targetAgentName) || targetAgentName
      wsTrace('switchSession', {
        sessionKey,
        targetAgentName,
        targetAgentId,
        storeAgentId: state.currentAgentId,
        wsAgentId: sharedWs ? sharedWs.agentId : null,
        forceReload: !!forceReload
      })
      if (
        targetAgentName &&
        (targetAgentName !== state.currentAgentName ||
          targetAgentId !== state.currentAgentId)
      ) {
        commit('SAVE_CURRENT_AGENT_STATE')
        commit('SET_CURRENT_AGENT_NAME', {
          agentName: targetAgentName,
          agentId: targetAgentId
        })
        commit('RESTORE_AGENT_STATE', targetAgentId)
        // Switch agent: clear knowledge id and load new agent knowledge; switch state is per-session
        commit('SET_KNOWLEDGE_ID', '')
        dispatch('loadAgentKnowledge', targetAgentId)
        dispatch('loadModels', { _agentId: targetAgentId })
        // 更新共享 WebSocket 的 agentId，确保后续事件路由到正确的 agent
        if (sharedWs) {
          sharedWs.setAgentId(targetAgentId)
          wsTrace('switchSession.setAgentId', {
            targetAgentId,
            storeAgentId: state.currentAgentId,
            wsAgentId: sharedWs.agentId
          })
        }
      }
      // 再次切入当前正在活跃流式输出的同一会话（例如智能体页发送后跳转到会话页、URL 补上 sessionKey
      // 触发 _syncFromQuery -> switchSession）时直接返回，避免把 isStreaming/sessionIsLive 重置为 false。
      // 否则流式期间新创建的工具调用组会以 sessionIsLive=false 渲染而自动闭合，
      // 导致智能体对话的工具调用集合框不再自动展开（数字人对话页没有这步重置所以正常）。
      // forceReload 场景（切换会话记录）不跳过，确保切回的会话从服务端重新加载。
      if (
        !forceReload &&
        targetAgentId === state.currentAgentId &&
        sessionKey === state.currentSessionKey &&
        (state.isStreaming || state.sessionIsLive)
      ) {
        wsTrace('switchSession.skipStreaming', {
          targetAgentId,
          sessionKey,
          wsAgentId: sharedWs ? sharedWs.agentId : null
        })
        return
      }
      commit('AGENT_MUTATION', {
        agentId: targetAgentId,
        mutation: 'SET_CURRENT_SESSION_KEY',
        payload: sessionKey
      })
      commit('SET_IS_STREAMING', false)
      commit('SET_SESSION_IS_LIVE', false)
      const ad = state.agentData[targetAgentId]
      // forceReload：清空该会话的本地缓存，强制走 loadHistory 从服务端重新加载。
      // 这样 mock marker（[mock:profit-margin-summary] 等）始终来自服务端持久化数据，
      // 避免首次恢复后 marker 被替换为卡片 HTML 导致后续切回无法触发图表重建。
      // 但以下两种情况不清空缓存，避免内容丢失：
      // 1) 真实问答正在流式输出（assistant.isStreaming / toolCall.phase !== 'result'）：
      //    服务端历史快照尚未落盘完整流式内容，清空后走 loadHistory 会丢失工具调用及已输出文本。
      // 2) demo 会话存在本地未持久化消息（!isHistory）：
      //    demo 流式输出由前端定时器模拟，中间卡片未持久化到服务端，清空后无法恢复。
      const targetCache = ad && ad.sessionMessages && ad.sessionMessages[sessionKey]
      const targetHasStreaming =
        targetCache &&
        targetCache.some(
          (m) =>
            (m.type === 'assistant' && m.isStreaming) ||
            (m.type === 'toolCall' && m.phase !== 'result')
        )
      const targetHasLocalLive =
        targetCache && targetCache.some((m) => !m.isHistory)
      if (forceReload && targetCache && !targetHasStreaming && !targetHasLocalLive) {
        ad.sessionMessages[sessionKey] = []
      }
      const cached = ad && ad.sessionMessages && ad.sessionMessages[sessionKey]
      if (cached && cached.length) {
        const cachedStreamingFlag =
          ad.sessionIsStreaming && ad.sessionIsStreaming[sessionKey]
        // 缓存中是否仍存在未结束的流式消息（assistant 流式 / 未完成的工具调用）
        const hasLiveStreaming =
          cached.some((m) => m.type === 'assistant' && m.isStreaming) ||
          cached.some((m) => m.type === 'toolCall' && m.phase !== 'result')
        const isStreaming = !!(cachedStreamingFlag && hasLiveStreaming)
        if (isStreaming) {
          // 正在流式输出中的会话：直接取缓存的消息（服务端历史快照不含流式内容）
          commit('SET_MESSAGES', [...cached])
          commit('SET_IS_STREAMING', true)
          commit('SET_SESSION_IS_LIVE', true)
          commit(
            'SET_CURRENT_RUN_ID',
            (ad.sessionRunIds && ad.sessionRunIds[sessionKey]) || null
          )
        } else {
          // 非流式但有本地缓存：直接展示缓存，避免服务端历史尚未落盘导致刚结束的消息丢失
          const cleaned = cached.map((m) =>
            m.isStreaming ? { ...m, isStreaming: false } : m
          )
          // 兜底：把缓存中仍未结束的工具调用标记为已完成，避免切回后一直转圈
          cleaned.forEach((m) => {
            if (m.type === 'toolCall' && m.phase !== 'result') {
              m.phase = 'result'
              m.isStreaming = false
              if (m.toolName) m.content = `Tool ${m.toolName} completed`
            }
          })
          ad.sessionMessages[sessionKey] = cleaned
          // 必须同时复位 agentData 缓存状态，否则下次 RESTORE_AGENT_STATE 又会卡“思考中”
          ad.sessionIsStreaming[sessionKey] = false
          ad.sessionRunIds[sessionKey] = null
          commit('SET_MESSAGES', [...cleaned])
          commit('SET_IS_STREAMING', false)
          commit('SET_CURRENT_RUN_ID', null)
          commit('SET_IS_FINISHING', false)
        }
      } else {
        commit('SET_MESSAGES', [])
        dispatch('loadHistory', {
          _agentName: targetAgentName,
          _agentId: targetAgentId,
          sessionKey
        })
      }
    },

    deleteSession({ state, commit, dispatch }, { sessionKey, agentName, agentId } = {}) {
      const targetAgentName = agentName || state.currentAgentName
      const targetAgentId = agentId || getAgentIdFromName(targetAgentName) || targetAgentName
      const ws = getEffectiveWs(targetAgentId)
      if (ws) {
        ws.sendRequest('sessions.delete', { key: sessionKey }).catch((err) => {
          console.error(`删除会话失败 [${targetAgentName}]:`, err)
        })
      }
      const ad = state.agentData[targetAgentId]
      if (ad) {
        const sessions = (ad.sessions || []).filter((s) => s.key !== sessionKey)
        commit('AGENT_MUTATION', {
          agentId: targetAgentId,
          mutation: 'SET_SESSIONS',
          payload: sessions
        })
        delete ad.sessionMessages[sessionKey]
        if (ad.currentSessionKey === sessionKey) {
          commit('AGENT_MUTATION', {
            agentId: targetAgentId,
            mutation: 'SET_CURRENT_SESSION_KEY',
            payload: null
          })
        }
        if (targetAgentName === state.currentAgentName) {
          commit('SET_MESSAGES', [])
        }
        dispatch('mergeAllSessions')
      }
    },

    updateSessionLabel({ state, commit, dispatch }, { sessionKey, label, agentName, agentId } = {}) {
      const targetAgentName = agentName || state.currentAgentName
      const targetAgentId = agentId || getAgentIdFromName(targetAgentName) || targetAgentName
      const ws = getEffectiveWs(targetAgentId)
      if (ws) {
        ws.sendRequest('sessions.patch', { key: sessionKey, label }).catch((err) => {
          console.error(`更新会话标签失败 [${targetAgentName}]:`, err)
        })
      }
      const ad = state.agentData[targetAgentId]
      if (ad) {
        const sessions = (ad.sessions || []).map((s) =>
          s.key === sessionKey ? { ...s, label } : s
        )
        commit('AGENT_MUTATION', {
          agentId: targetAgentId,
          mutation: 'SET_SESSIONS',
          payload: sessions
        })
        dispatch('mergeAllSessions')
      }
    },

    finishStreamingMessage({ state, commit }, { _agentName, _agentId, _sessionKey } = {}) {
      const targetAgentId = _agentId || getAgentIdFromName(_agentName) || ''
      if (targetAgentId) {
        // 定向收尾某个 agent 的会话（含后台路由会话），同步清理其 agentData 缓存
        const ad = state.agentData[targetAgentId]
        if (!ad) return
        const sk = _sessionKey || ad.currentSessionKey
        if (sk) {
          if (ad.sessionMessages[sk]) {
            ad.sessionMessages[sk].forEach((m) => {
              if (m.isStreaming) {
                m.isStreaming = false
                if (m.content) m.content = stripSilentMarkers(m.content)
              }
              if (m.type === 'toolCall' && m.phase !== 'result') {
                m.phase = 'result'
                if (m.toolName) m.content = `Tool ${m.toolName} completed`
              }
            })
          }
          ad.sessionIsStreaming[sk] = false
          ad.sessionRunIds[sk] = null
        }
        ad.isAborting = false
        ad.isFinishing = false
        ad.lastAssistantText = ''
        ad.agentEnded = false
        // 仅当收尾的是当前正在查看的会话时同步 flat state，避免串台
        const isCurrentSession =
          targetAgentId === state.currentAgentId &&
          (!sk || sk === state.currentSessionKey)
        if (isCurrentSession) {
          if (state.messages.some((m) => m.isStreaming)) {
            state.messages.forEach((m) => {
              if (m.isStreaming) {
                m.isStreaming = false
                if (m.content) m.content = stripSilentMarkers(m.content)
              }
            })
          }
          state.messages.forEach((m) => {
            if (m.type === 'toolCall' && m.phase !== 'result') {
              m.phase = 'result'
              if (m.toolName) m.content = `Tool ${m.toolName} completed`
            }
          })
          commit('SET_IS_STREAMING', false)
          commit('SET_CURRENT_RUN_ID', null)
          commit('SET_SESSION_IS_LIVE', false)
          commit('SET_IS_FINISHING', false)
          state.isAborting = false
          state.lastAssistantText = ''
          state.agentEnded = false
        }
        return
      }
      // 未指定 agentId：收尾当前 agent 的会话，同时清理其 agentData 缓存
      const curAgentId = state.currentAgentId
      const sk = state.currentSessionKey
      const ad = curAgentId ? state.agentData[curAgentId] : null
      if (ad && sk) {
        if (ad.sessionMessages[sk]) {
          ad.sessionMessages[sk].forEach((m) => {
            if (m.isStreaming) {
              m.isStreaming = false
              if (m.content) m.content = stripSilentMarkers(m.content)
            }
            if (m.type === 'toolCall' && m.phase !== 'result') {
              m.phase = 'result'
              if (m.toolName) m.content = `Tool ${m.toolName} completed`
            }
          })
        }
        ad.sessionIsStreaming[sk] = false
        ad.sessionRunIds[sk] = null
      }
      const hasResidualStreaming = state.messages.some((m) => m.isStreaming)
      if (!state.isStreaming && !state.isFinishing && !hasResidualStreaming) return
      commit('SET_IS_FINISHING', true)
      commit('SET_STREAMING_DONE')
    },

    storeMockMessage({ state, commit, dispatch }, { role, content, richHtml = false }) {
      const sessionKey = state.currentSessionKey
      if (!sessionKey) return
      // 提取 mock scenario 标识并持久化到消息字段，
      // 即使后续 content 被替换为卡片 HTML，仍能在切换会话时重新触发恢复渲染（含 ECharts 图表重建）
      var mockScenario = ''
      var mockMatch = typeof content === 'string' ? content.match(/\[mock:([^\]]+)\]/) : null
      if (mockMatch) mockScenario = mockMatch[1]
      const message = {
        id: 'mock-' + Date.now() + '-' + Math.random().toString(36).slice(2),
        type: role,
        content: content,
        timestamp: Date.now(),
        isHistory: false,
        richHtml: richHtml,
        _mockScenario: mockScenario
      }
      commit('AGENT_MUTATION', {
        agentId:
          state.currentAgentId ||
          getAgentIdFromName(state.currentAgentName) ||
          state.currentAgentName,
        mutation: 'ADD_MESSAGE',
        payload: message,
        _sessionKey: sessionKey
      })
      var colonIdx = sessionKey.indexOf(':', sessionKey.indexOf(':') + 1) + 1
      var conversationId = colonIdx > 0 ? sessionKey.slice(colonIdx) : sessionKey
      const agentId = state.currentAgentId
      const persistTask = mockMessagePersistQueue
        .then(function () {
          return appendMockMessageApi(
            conversationId,
            role,
            content,
            agentId
          )
        })
        .then(function () {
          return dispatch('loadSessions')
        })
        .catch(function (err) {
          console.warn('[mockStore] mock message persistence failed:', err)
        })
      mockMessagePersistQueue = persistTask.catch(function () {})
      return persistTask
    }
  }
}
function parseHistoryMessages(rawMessages) {
  const messages = []
  rawMessages.forEach((msg) => {
    if (msg.role === 'user') {
      let text = ''
      if (Array.isArray(msg.content)) {
        text = msg.content
          .filter((c) => c.type === 'text' && c.text)
          .map((c) => c.text)
          .join('')
      } else if (typeof msg.content === 'string') {
        text = msg.content
      }
      if (text) {
        if (text.startsWith('[iframe-response]')) return
        const lines = text.split('\n')
        const attachments = []
        const contentLines = []
        const urlPattern = /^https?:\/\/.+/i
        for (const line of lines) {
          const trimmed = line.trim()
          if (urlPattern.test(trimmed)) {
            const urlPath = trimmed.split('?')[0].split('#')[0]
            const lastSegment = urlPath.split('/').pop() || ''
            const hasFileExt = /\.[a-z0-9]{2,5}$/i.test(lastSegment)
            if (hasFileExt) {
              attachments.push({ name: lastSegment, url: trimmed })
            } else {
              contentLines.push(line)
            }
          } else {
            contentLines.push(line)
          }
        }
        const cleanContent = contentLines.join('\n').trim()
        messages.push({
          id: generateUUID(),
          type: 'user',
          content: cleanContent,
          timestamp: msg.timestamp || Date.now(),
          isHistory: true,
          attachments: attachments.length > 0 ? attachments : undefined
        })
      }
    } else if (msg.role === 'assistant') {
      if (Array.isArray(msg.content)) {
        let leadingText = ''
        const textParts = []
        const hasMixedContent = msg.content.some((c) => c.type === 'toolCall')
        msg.content.forEach((c) => {
          if (c.type === 'text' && c.text) {
            const cleaned = stripSilentMarkers(c.text)
            if (cleaned) textParts.push(cleaned)
          } else if (c.type === 'thinking' && c.thinking) {
            messages.push({
              id: generateUUID(),
              type: 'thinking',
              content: c.thinking,
              timestamp: msg.timestamp || Date.now(),
              isHistory: true
            })
          } else if (
            (c.type === 'toolCall' || c.type === 'function' || c.type === 'tool_use') &&
            (c.name || (c.function && c.function.name) || c.toolName)
          ) {
            const toolName = c.name || (c.function && c.function.name) || c.toolName || 'unknown'
            if (hasMixedContent && textParts.length > 0 && !leadingText) {
              leadingText = textParts.join('\n\n')
              textParts.length = 0
              messages.push({
                id: generateUUID(),
                type: 'assistant',
                content: leadingText,
                timestamp: msg.timestamp || Date.now(),
                isHistory: true
              })
            }
            const toolArgs = c.arguments || c.args || (c.function && c.function.arguments) || c.input || (c.function && c.function.input) || c.parameters || ''
            const toolResult = c.content || c.result || c.output || c.response || ''
            const toolCompleted = !!(toolResult && toolResult !== '')
            messages.push({
              id: generateUUID(),
              type: 'toolCall',
              content: c.thought ? 'Thinking: ' + c.thought : toolCompleted ? 'Tool ' + toolName + ' completed' : 'Calling tool: ' + toolName,
              toolName: toolName,
              thought: c.thought || '',
              toolCallId: c.toolCallId || c.id || toolName + '-' + (msg.timestamp || Date.now()),
              phase: 'result',
              args: typeof toolArgs === 'object' ? JSON.stringify(toolArgs, null, 2) : toolArgs || '',
              result: typeof toolResult === 'object' ? JSON.stringify(toolResult, null, 2) : toolResult || '',
              isStreaming: false,
              isError: false,
              timestamp: msg.timestamp || Date.now(),
              isHistory: true
            })
          } else if (c.type === 'toolResult' || c.type === 'tool_result') {
            const resultMsg = messages[messages.length - 1]
            if (resultMsg && resultMsg.type === 'toolCall' && (!resultMsg.result || resultMsg.result === '')) {
              resultMsg.phase = 'result'
              resultMsg.result = typeof c.content === 'object' ? JSON.stringify(c.content, null, 2) : c.content || ''
            }
          }
        })
        if (textParts.length > 0) {
          messages.push({
            id: generateUUID(),
            type: 'assistant',
            content: textParts.join('\n\n'),
            timestamp: msg.timestamp || Date.now(),
            isHistory: true
          })
        }
      } else if (typeof msg.content === 'string') {
        const cleaned = stripSilentMarkers(msg.content)
        if (cleaned) {
          messages.push({
            id: generateUUID(),
            type: 'assistant',
            content: cleaned,
            timestamp: msg.timestamp || Date.now(),
            isHistory: true
          })
        }
      }
    } else if (msg.role === 'toolResult') {
      const lastMsg = messages[messages.length - 1]
      if (lastMsg && lastMsg.type === 'toolCall') {
        lastMsg.phase = 'result'
        lastMsg.result = typeof msg.content === 'object'
          ? JSON.stringify(msg.content, null, 2)
          : typeof msg.content === 'string'
            ? msg.content
            : msg.content || ''
        lastMsg.isError = msg.isError || false
      }
    }
  })
  return messages
}
export default cosmoClaw
